/**
 * Persisted dual-write outbox (ATL-782 / ATL-776 ask 1).
 *
 * WHY THIS EXISTS: `bestEffortDualWrite` fires AFTER Firestore has committed, so a failed
 * Supabase leg is a write that already happened in the source of truth and nowhere else.
 * Until now a transient failure (timeout, 5xx, network, 401 not-yet-migrated) was logged at
 * `warning` and dropped, on the stated grounds that "they self-heal" — which was true only
 * while the reconciliation sweep ran, and the sweep is PAUSED (ATL-707). Measured cost:
 * 827 `sales.create` timeouts / 37 users in one week, biased toward the LARGEST carts
 * (ATL-776, Fisher p≈0.0025 at ресторан Марракеш). This module is the healing mechanism:
 * failed writes are persisted and re-issued until they land, bounded by attempts and age.
 *
 * WHY PERSISTED (not in-memory retry): the POS is used intermittently — median gap between
 * sales at the org that motivated this is 1,185s — so "retry a few times in this tick" dies
 * with the app long before the network recovers. AsyncStorage survives restarts; entries are
 * drained on the next launch/sale, which matches how the device is actually used.
 *
 * REPLAY SAFETY MODEL — every rule below exists because its absence corrupts books:
 *
 *  1. NO DOCUMENT IDENTITY ⇒ NO REPLAY. An entry is queued only when a Firestore document id
 *     is derivable (caller context, payload `firestoreId`/`id`, or the URL tail of an
 *     update/delete). The id is both the SUPERSEDE key and the backend's IDEMPOTENCY key —
 *     a payload without one (e.g. `clientOperations.create`, `auditLog.create`) would
 *     double-apply on a replay whose original actually committed server-side, so it is
 *     refused loudly instead of queued (`no-document-identity` breadcrumb).
 *
 *  2. ORDERING per document. Entries for one document drain strictly in enqueue order, and a
 *     transient failure PAUSES that document's remaining entries until the next round — an
 *     update can never overtake its own create. Independent documents drain independently.
 *
 *  3. SUPERSEDE / PURGE. A newer enqueue for the same (label, document) replaces the older
 *     entry. A live SUCCESS for a document purges its queued same-label entries — and a live
 *     DELETE success purges the document's queued entries across ALL labels, because
 *     replaying a create/update after the document was deleted would RESURRECT it in
 *     Supabase (a ghost sale, with stock movements). The live-success document id is taken
 *     from the API RESPONSE (`result.firestoreId`) as well as caller context, so the purge
 *     is active on every dual-write path, not only ones that pass explicit context.
 *
 *  4. RE-CHECK BEFORE REPLAY. The drain re-reads the queue immediately before each entry and
 *     skips entries that were purged after the drain snapshot was taken — a slow drain must
 *     not replay an entry whose document was deleted/rewritten mid-drain. (The residual race
 *     is the single in-flight request; the backend's idempotency-on-firestore_id covers it.)
 *
 *  5. DETERMINISTIC DEFECTS are never queued (`classifyDualWriteDefect` in dualWrite.ts),
 *     and a replay refused permanently (400/409/422) is dropped LOUDLY — a fingerprinted
 *     Sentry error, because a dropped entry is a permanent Supabase loss (ATL-664).
 *
 *  6. A 200 IS NOT AUTOMATICALLY A SUCCESS (ATL-962). `/products/stock/bulk` answers 200 with
 *     a per-item `results[].ok`, so "the replay did not throw" removed entries whose every item
 *     had failed — silently, with no drop record. A replay whose response reports failed items
 *     is treated as a failed replay: the entry is kept, an attempt is consumed, and it ends at
 *     `attempts-exhausted` → `reportDrop` like any other permanent loss. Endpoints with this
 *     shape are recognised in `stockMirrorResult.ts`; every other endpoint's HTTP status stays
 *     the whole verdict.
 *
 *  7. WEB, MULTI-TAB: drains take a Web Locks lease when `navigator.locks` exists, so two
 *     tabs cannot replay the same entry concurrently; queue mutations take the same lock.
 *     (RN has a single JS context; the in-process mutex below is sufficient there.)
 *
 *  8. A DELETE MAY ONLY FIRE WHILE THE ENTITY IS STILL GONE (ATL-1207). Rules 3 and 4 are fed
 *     entirely by writes made in THIS process, so a recreate on ANOTHER device is invisible to
 *     them and the replay removes a row the customer can see — for up to the seven days rule 3
 *     holds an entry. An entry whose caller stated `deletedFrom` therefore has that location
 *     re-read from FIRESTORE, the store every device converges on, immediately before the replay:
 *     present ⇒ the delete is withdrawn (loudly, `reportRecreated`), absent ⇒ replay unchanged,
 *     unreadable ⇒ held without consuming an attempt. Stated only where a document id can be
 *     REUSED — the user-typed constants families — and, as a FIELD-STATE precondition, on the user
 *     disable/enable legs (ATL-1209: valid only while `users/<doc>.active` still holds the value it
 *     set); see `deletedEntityLocation`. It also closes the
 *     same-device version of the gap, which rule 3 never covered: a live CREATE landing for the
 *     document purges only its SAME-LABEL entries, so a queued delete survived it. The verdict is
 *     only as fresh as the read behind it, and that read completes BEFORE the request it
 *     authorises, so a live write landing inside that window is checked separately
 *     (`liveWritesDuringDrain`) — otherwise the guard's own round-trip is the race.
 *
 *  9. A PRECONDITIONED WRITE IS A LINK OF A CHAIN (ATL-1503). An entry carrying `expectedPayment`
 *     is never replaced (rule 3's supersede) or purged (rule 3's live-success purge) by a later write
 *     of its document — only a delete of the document still supersedes it — and replaces nothing
 *     itself; a live preconditioned write whose document has queued entries is queued behind them
 *     instead of being sent (capped: see `queuedBehindDocument` in dualWrite.ts). See
 *     `isPreconditionedRequest`.
 *
 * 10. A CREDENTIAL WRITE IS NEVER PERSISTED (ATL-1951). A password change is valid only at the
 *     moment it was made: replayed later it is a stale credential write — in prod an 11.5-hour-old
 *     `users.updatePassword` replay signed a till out, and one replayed after a newer change would
 *     revert the account to the older password — and its payload was the password, in plaintext,
 *     at rest. `isCredentialWrite` entries are refused at enqueue and PURGED on load, which is what
 *     removes the ones bundles before ATL-1951 already wrote. (The backend route has also stopped
 *     setting the password, so a replay from a device still on an old bundle is harmless too.)
 *
 * PII note: entries persist request payloads in AsyncStorage — the same device already holds
 * the same data in Firestore's offline cache, and payloads are never attached to Sentry
 * events (ids only). Credentials are the exception that is never persisted at all (rule 10).
 */
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as Sentry from '@sentry/react-native'
import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import type { ReplayableRequestConfig } from '@atlas/api-client'

import {
  failedBulkStockItems,
  isBulkStockMirrorRequest,
  reportFailedStockMirrorItems,
} from './stockMirrorResult'
import type { DeletedEntityLocation } from './deletedEntityLocation'
// ATL-1487: the status extraction lives with the refusal classifier so the live path (dualWrite.ts)
// and this replay path read a status the same way; `replayFailureStatus` is its name here.
import {
  failureStatus as replayFailureStatus,
  isRefusedWriteFailure,
  refusalCode,
  reportRefusedWrite,
  UNCODED_REFUSAL,
} from './dualWriteRefusal'

// ============================================================================
// Tuning
// ============================================================================

const STORAGE_KEY = 'dualWriteOutbox.v1'
/** Hard cap on queued entries — beyond this the oldest DOCUMENT GROUP is dropped (loudly). */
const MAX_ENTRIES = 100
/** A single entry larger than this is not queued (loudly) — AsyncStorage is not a blob store. */
const MAX_ENTRY_BYTES = 256 * 1024
/**
 * Cap on the whole serialized queue. Web AsyncStorage is localStorage (~5MB quota shared with
 * everything else on the origin); exceeding it makes `setItem` throw and the enqueue fail.
 */
const MAX_TOTAL_BYTES = 2 * 1024 * 1024
/**
 * Retries per entry before giving up (loudly). With the backoff below the ten are spent in ~8.5 h of
 * backoff at the fastest (30 s · 2^n for n = 1…9; the 6 h cap is never reached) — which is why every
 * failure class whose fix is slower than that holds without consuming one (ATL-902, ATL-908, ATL-911,
 * ATL-1805, ATL-1204) and is bounded by MAX_AGE_MS instead.
 */
const MAX_ATTEMPTS = 10
/** Entries older than this are expired (loudly) — after a week the sweep/re-migration owns it. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** Backoff: BASE * 2^attempts, capped. First retry ~30s, then 1m, 2m, … cap 6h. */
const BASE_BACKOFF_MS = 30_000
const MAX_BACKOFF_MS = 6 * 60 * 60 * 1000
/**
 * ATL-902 hold interval for a dead BACKEND HOST. Flat, not exponential, and it consumes no
 * attempt (see the drain): the failure is environmental and identical every time, so the entry
 * is simply parked until either the device updates or MAX_AGE_MS expires it. Same 6h as the
 * backoff ceiling — ~28 cheap probes across the week rather than 10 inside half a day.
 */
const DEAD_BACKEND_RETRY_MS = MAX_BACKOFF_MS
/**
 * ATL-908 — first retry delay after an auth refusal. Deliberately the same 30s base as a normal
 * transient failure and NOT the dead-backend 6h: a session can become authorized within seconds
 * (auth restoring at app start), and a 6h floor would make the app-start and `org-ready` drains —
 * the very moments auth becomes usable — unable to act. It doubles per consecutive hold to the
 * same 6h cap, so a genuinely unmigrated user costs ~40 requests across the 7-day age bound
 * rather than thousands.
 */
const AUTH_RETRY_BASE_MS = BASE_BACKOFF_MS
/**
 * ATL-1805 — base of the retry delay after a `migration_paused` refusal: the n-th pause hold waits
 * `base × 2^n` (60s for the first, as for the auth and conversion holds), capped at
 * {@link MIGRATION_PAUSE_RETRY_CAP_MS}. A pause lasts as long as the organization's re-migration
 * (minutes to a few hours; the backend lease is 2h and re-armed while the run advances), so the cap is
 * kept well below the 6h backoff ceiling: once the pause lifts, a held write lands within 15 minutes
 * (or at the next app-start / org-ready drain) instead of up to six hours later.
 */
const MIGRATION_PAUSE_RETRY_BASE_MS = BASE_BACKOFF_MS
const MIGRATION_PAUSE_RETRY_CAP_MS = 15 * 60 * 1000
/**
 * ATL-1204 — cap of the retry delay after a server fault (`isServerFaultFailure`): the n-th server
 * hold waits `BASE_BACKOFF_MS × 2^n` (60 s for the first), capped here rather than at the 6 h
 * MAX_BACKOFF_MS. Nothing retires a server hold when the backend recovers (a success elsewhere is no
 * proof for a payload the backend may be failing deterministically — the ATL-1137 lesson), so the
 * cap IS the worst-case delay between the backend healing and a held write landing. At 6 h a till
 * whose sales were held through a working-day outage would show them missing from Supabase-read
 * screens for up to six more hours after the fix; at 1 h, at most one. The price is the request
 * rate of an entry the backend fails forever: ~170 requests across the 7-day MAX_AGE_MS instead of
 * ~35 — per held document group, only while the app runs, and only against a backend that is
 * already answering 5xx, which it does at once.
 *
 * NOT for a client TIMEOUT, which is held on the same counter but keeps the 6 h MAX_BACKOFF_MS cap
 * (`serverFaultRetryCapMs`). A timeout costs the drain the full write timeout (60 s) and the drain
 * runs one document at a time, so a replay that times out every time — the largest carts, ATL-776 —
 * would hold every other document's replay back for a minute each hour. At 6 h that is the
 * ~35 tries of the old attempt-consuming path, whose worst wait (4.27 h) was already of that order.
 */
const SERVER_FAULT_RETRY_CAP_MS = 60 * 60 * 1000
/** ATL-1204 — the cap a server hold's backoff doubles to: see SERVER_FAULT_RETRY_CAP_MS. */
function serverFaultRetryCapMs(error: unknown): number {
  return isClientTimeoutFailure(error) ? MAX_BACKOFF_MS : SERVER_FAULT_RETRY_CAP_MS
}
/** Successive drains are coalesced within this window (except interval/manual triggers). */
const DRAIN_THROTTLE_MS = 15_000
/**
 * Idle-device healing tick. Armed by ANY drain that leaves entries behind, stopped when a drain
 * finds the queue empty.
 *
 * ATL-983: it used to be armed only by `enqueueDualWriteFailure`, i.e. only when a NEW failure
 * was queued in this process. A device that queued writes yesterday and made no dual-write today
 * therefore never started the timer and never touched its queue — which is precisely the device
 * with an unhealed queue. Arming it from the drain's own tail makes the tick a property of the
 * QUEUE being non-empty rather than of a write having just failed.
 */
export const DRAIN_INTERVAL_MS = 5 * 60 * 1000
/**
 * How often a drain that healed nothing may report itself. The condition can persist for days
 * (an offline till), and one event per tick would be 288/device/day.
 */
const DRAIN_STALL_REPORT_INTERVAL_MS = 60 * 60 * 1000
/**
 * How old the oldest DUE entry must be before a fruitless drain is worth an event.
 *
 * A drain that replays nothing is not by itself a fault: one transient failure backing off is the
 * outbox working exactly as designed, and alerting on it would bury the real signal in the noise
 * this module exists to escape. What is diagnostic is a queue that is NOT CONVERGING — entries
 * still due a day after they were queued, against a 7-day MAX_AGE_MS. That is the shape the
 * evidence had (entries at 168–456h with 0–1 attempts) and the shape nothing was reporting.
 */
const DRAIN_STALL_MIN_AGE_MS = 24 * 60 * 60 * 1000
/** Web Locks name shared by every tab of the origin (web only). */
const WEB_LOCK_NAME = 'atlas-dual-write-outbox'

// ============================================================================
// Types
// ============================================================================

export interface OutboxEntry {
  id: string
  /** `module.op` label of the originating dual-write (e.g. `sales.create`). */
  label: string
  request: ReplayableRequestConfig
  /** Firestore document id — supersede key + the backend's idempotency key. Always present. */
  firestoreId: string
  /** Org the write belongs to (Firestore id) — drain skips entries from another org. */
  orgFirestoreId: string
  enqueuedAt: number
  attempts: number
  /**
   * ATL-908 — auth refusals (401/403) back off on their OWN counter and never touch `attempts`.
   * A 401 is a verdict on the SESSION, not on the payload: the server rejected the caller before
   * it ever read the body, so counting it toward the drop budget destroys a write for a reason
   * that has nothing to do with the write. Optional because entries persisted by earlier bundles
   * do not carry it — absent reads as 0.
   */
  authHolds?: number
  /**
   * ATL-1137 — this entry's last refusal was a 403, i.e. a verdict on the caller's ROLE for THIS
   * endpoint, not on the session. It survives the ATL-908 hold retirement below, which a 401 does
   * not. Optional because entries persisted by earlier bundles do not carry it — absent reads as
   * false, which is the pre-ATL-1137 behaviour.
   */
  forbidden?: boolean
  /**
   * ATL-1207 — where in Firestore the entity this write DELETED lived, when the caller states it.
   *
   * It is the entry's own precondition: this write is valid only while that entity is still gone.
   * The drain re-reads the location from the SERVER immediately before replaying, because neither
   * this queue nor the live path can see a recreate performed on ANOTHER device — and a delete
   * replayed over one destroys a row the customer can see. Absent on every entry whose id can
   * never be reused (Firestore mints those) and on entries persisted by earlier bundles; absent
   * means "replay as before", i.e. exactly the pre-ATL-1207 behaviour.
   */
  deletedFrom?: DeletedEntityLocation
  /**
   * ATL-1207 — how many times the recreate probe went UNANSWERED for this entry (the device is
   * offline, the org is not the session's, Firestore refused the read). Its own counter for the
   * same reason `authHolds` is one: nothing about the payload failed, so counting it toward the
   * drop budget would destroy a write for a reason that has nothing to do with it. Bounded by
   * MAX_AGE_MS like every other hold.
   *
   * Cumulative over the entry's life rather than consecutive — unlike `authHolds`, nothing retires
   * it. There is nothing for a retirement to observe: a probe that DOES answer is followed
   * immediately by the replay it authorised, which either removes the entry or consumes an
   * attempt, so a stale count can only make a later hold's backoff more conservative (capped at
   * MAX_BACKOFF_MS), never less.
   */
  probeHolds?: number
  /**
   * ATL-1445 stage 3, P8 — how many times this entry's conversion into outbox v2 could not be
   * persisted after the backend refused it as a Firestore-first mirror of a Supabase-master
   * organization (409 `WRITE_MASTER_SUPABASE`, backend PR B1). Its own counter for the same reason as
   * `authHolds` and `probeHolds`: nothing about the payload failed — the device's v2 store did — so it
   * never consumes an attempt. Set only once an organization is Supabase-master; absent on every entry today.
   */
  conversionHolds?: number
  /**
   * ATL-1805 — how many times the backend answered `503 migration_paused` (the organization
   * is being re-migrated and its dual-write is paused; nothing executed). Its own counter for the same
   * reason as `authHolds`: nothing about the payload failed, so it never consumes an attempt. Cumulative,
   * like `probeHolds`: nothing retires it, and a stale count can only lengthen a later pause hold's delay
   * up to its 15-minute cap. Bounded by MAX_AGE_MS. Absent on entries that were never paused and on
   * entries persisted by earlier bundles — absent reads as 0.
   */
  pauseHolds?: number
  /**
   * ATL-1204 — how many times the backend answered a SERVER FAULT (5xx other than the ATL-1805 pause).
   * Its own counter for the reason `isServerFaultFailure` gives: a server fault says nothing about
   * whether this write can succeed, and what fixes it — an operator restoring the database, a
   * deploy — is slower than the attempt budget. Cumulative, like `probeHolds`: a stale count can only
   * lengthen a later hold's delay up to its cap (`serverFaultRetryCapMs`). Bounded by MAX_AGE_MS. Absent
   * reads as 0.
   */
  serverHolds?: number
  nextAttemptAt: number
  lastError?: string
  /**
   * ATL-1760 — WHAT kind of failure `lastError` was, as an enum (`replayFailureClass`). The string
   * never leaves the device; this is the part that can: an entry that expires after a week of holds
   * reports which hold it was in, which is the one fact that separates "this till cannot reach the
   * API" from "the API kept timing out". Absent on entries written before it existed, and on entries
   * whose `lastError` is not a failure (queued behind another write, a recovered intent).
   */
  lastErrorClass?: ReplayFailureClass
}

/**
 * ATL-1760 — a dual-write failure reduced to the enum it is reported as. Every value is decided
 * from the error's structure (status, body, axios transport fields), never from free text the
 * server chose, so it is safe to send off the device and stable enough to group by.
 */
const REPLAY_FAILURE_CLASSES = [
  'dead-backend',
  'route-404',
  'transport',
  'migration-paused',
  'timeout',
  'unauthorized',
  'forbidden',
  'not-found',
  'refused',
  'client-validation',
  'server-error',
  'http-other',
  'other',
] as const
export type ReplayFailureClass = (typeof REPLAY_FAILURE_CLASSES)[number]

export type OutboxDropReason =
  | 'permanent-refusal'
  | 'attempts-exhausted'
  | 'expired'
  | 'queue-overflow'
  | 'entry-too-large'

/**
 * `app-start` and `org-ready` (ATL-983) are the only triggers that do not require a dual-write to
 * have happened in this process — they are what makes the queue drain on a device that queued
 * writes and then stopped selling.
 */
type DrainTrigger =
  | 'app-start'
  | 'org-ready'
  | 'launch'
  | 'live-success'
  | 'enqueue'
  | 'interval'
  | 'manual'
  /**
   * ATL-1459: a mirror intent's write was witnessed in Firestore and its request queued
   * (`enqueueRecoveredMirrorIntent`). Recovery runs at `org-ready`, right after that edge's own drain,
   * so a throttled trigger would leave the entry for the idle tick.
   */
  | 'recovered-intent'
  /**
   * ATL-1445 stage 3, P8: a live mirror was refused by backend PR B1 (409 `WRITE_MASTER_SUPABASE`) and
   * queued here (`enqueueRefusedMirror`); this drain re-sends it and converts it into outbox v2. Never
   * fired while no organization is Supabase-master.
   */
  | 'master-refusal'

/** Triggers that are deliberate and infrequent, so the coalescing window must not swallow them. */
const UNTHROTTLED_TRIGGERS: ReadonlySet<DrainTrigger> = new Set<DrainTrigger>([
  'interval',
  'manual',
  'app-start',
  'org-ready',
  'recovered-intent',
  'master-refusal',
])

// ============================================================================
// Cross-context locking (web multi-tab) + in-process mutex
// ============================================================================

type LockManagerLike = {
  request: (
    name: string,
    options: { ifAvailable?: boolean },
    cb: (lock: unknown) => Promise<unknown>,
  ) => Promise<unknown>
}

function webLocks(): LockManagerLike | null {
  try {
    const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator
    return nav?.locks ?? null
  } catch {
    return null
  }
}

let queueMutex: Promise<unknown> = Promise.resolve()

/**
 * Serialize every read-modify-write of the queue. In-process via a promise chain; across web
 * tabs via a Web Lock around the storage RMW (localStorage writes are last-writer-wins, so
 * two tabs' whole-array saves would otherwise clobber each other and resurrect removed
 * entries).
 */
function withQueue<T>(fn: (queue: OutboxEntry[]) => Promise<{ queue: OutboxEntry[]; result: T }>): Promise<T> {
  const rmw = async (): Promise<T> => {
    const queue = await loadQueue()
    const { queue: next, result } = await fn(queue)
    await saveQueue(next)
    return result
  }
  const run = queueMutex.then(async () => {
    const locks = webLocks()
    if (locks) {
      return locks.request(WEB_LOCK_NAME, {}, rmw) as Promise<T>
    }
    return rmw()
  })
  // The chain must survive a failed operation — swallow for sequencing only.
  queueMutex = run.catch(() => undefined)
  return run
}

async function loadQueue(): Promise<OutboxEntry[]> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const entries = parsed.filter(isOutboxEntry)
    // Rule 10 (ATL-1951): a bundle before ATL-1951 persisted password changes, with the password.
    // Dropped here, so the next save — every drain starts with one — removes them from storage.
    const kept = entries.filter((e) => !isCredentialWrite(e.label, e.request))
    if (kept.length !== entries.length) reportCredentialPurge(entries.length - kept.length)
    return kept
  } catch (error) {
    // A corrupt queue must not take the sale path down — reset, but say so.
    // eslint-disable-next-line no-console
    console.error('[dualWriteOutbox] corrupt queue — resetting', errorMessage(error))
    Sentry.captureMessage('dual-write outbox corrupt — queue reset', {
      level: 'error',
      tags: { dual_write_outbox: 'corrupt' },
      fingerprint: ['dual-write-outbox-corrupt'],
    })
    return []
  }
}

async function saveQueue(queue: OutboxEntry[]): Promise<void> {
  if (queue.length === 0) {
    await AsyncStorage.removeItem(STORAGE_KEY)
    return
  }
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
}

function isOutboxEntry(value: unknown): value is OutboxEntry {
  if (!value || typeof value !== 'object') return false
  const e = value as Record<string, unknown>
  return (
    typeof e.id === 'string' &&
    typeof e.label === 'string' &&
    typeof e.firestoreId === 'string' &&
    typeof e.orgFirestoreId === 'string' &&
    typeof e.enqueuedAt === 'number' &&
    typeof e.attempts === 'number' &&
    typeof e.nextAttemptAt === 'number' &&
    !!e.request &&
    typeof (e.request as Record<string, unknown>).method === 'string' &&
    typeof (e.request as Record<string, unknown>).url === 'string'
  )
}

// ============================================================================
// Document identity & label semantics
// ============================================================================

/** `sales.delete`, `cashboxTransactions.delete`, `sales.deleteSaleReturn`, … */
export function isDeleteLabel(label: string): boolean {
  const op = label.slice(label.indexOf('.') + 1)
  return op === 'delete' || op.startsWith('delete')
}

/** `PATCH /users/:id/password` — the only credential route a dual-write has ever called. */
const CREDENTIAL_ROUTE = /^\/?users\/[^/]+\/password\/?$/

/**
 * Rule 10 (ATL-1951) — a write whose replay would be a credential change, or whose payload is one.
 * Matched by label AND by route, so an entry is caught whichever of the two a future caller spells
 * differently. Such an entry is never persisted: see the header.
 */
export function isCredentialWrite(label: string, request: ReplayableRequestConfig | undefined): boolean {
  if (label === 'users.updatePassword') return true
  if (!request || typeof request.url !== 'string') return false
  return CREDENTIAL_ROUTE.test(request.url.split('?')[0])
}

/**
 * Rule 10 — legacy credential entries were removed on load. Not a loss to alert on: the credential
 * was set live by the callable, and what is given up is one `updated_by` stamp. Counted, so the
 * purge of entries written by old bundles is measurable; no payload, id or label detail beyond it.
 *
 * SELF-GUARDED: it runs inside `loadQueue`'s `try`, whose `catch` treats any throw as a corrupt
 * queue and returns `[]` — which the caller then SAVES, deleting every other entry (sales included).
 * A reporter must never be able to do that.
 */
function reportCredentialPurge(count: number): void {
  try {
    // eslint-disable-next-line no-console
    console.warn('[dualWriteOutbox] purged persisted credential writes (ATL-1951)', JSON.stringify({ count }))
    Sentry.addBreadcrumb({
      category: 'dual_write.outbox',
      level: 'warning',
      message: 'outbox: purged persisted credential writes (ATL-1951)',
      data: { count },
    })
    captureOperationalEvent('dual_write_outbox_credential_purged', { count })
  } catch {
    // Telemetry only; the purge itself has already happened in the caller.
  }
}

/**
 * Firebase push/document ids are ≥15 URL-safe ALPHANUMERIC chars (typically 20/28) — no
 * dashes, which is what keeps kebab-case route words (`loyalty-settings`,
 * `marketplace-settings`) from ever matching. Supabase UUIDs are matched by their exact
 * shape instead.
 */
const FIRESTORE_ID_SEGMENT = /^[A-Za-z0-9]{15,}$/
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isDocIdSegment(segment: string | undefined): segment is string {
  return Boolean(segment && (FIRESTORE_ID_SEGMENT.test(segment) || UUID_SEGMENT.test(segment)))
}

/**
 * Derive the Firestore document id a request is about — the supersede/idempotency key.
 * Sources, in order: caller context → payload `firestoreId` → payload `id` → for
 * update/delete methods, the LAST id-shaped URL path segment. Scanning from the end covers
 * both `/sales/:id` and subresource shapes like `/products/:id/stock` (review R4 — that
 * PATCH is an idempotent absolute-stock write and must stay outbox-eligible).
 */
export function deriveDocumentId(
  request: ReplayableRequestConfig,
  contextFirestoreId?: string,
): string | undefined {
  if (contextFirestoreId) return contextFirestoreId
  const data = request.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>
    if (typeof d.firestoreId === 'string' && d.firestoreId) return d.firestoreId
    if (typeof d.id === 'string' && d.id) return d.id
  }
  if (request.method === 'PATCH' || request.method === 'PUT' || request.method === 'DELETE') {
    const segments = request.url.split('?')[0].split('/').filter(Boolean)
    for (let i = segments.length - 1; i >= 0; i--) {
      if (isDocIdSegment(segments[i])) return segments[i]
    }
  }
  return undefined
}

// ============================================================================
// Failure classification (for the REPLAY path — the live path classifies in dualWrite.ts)
// ============================================================================

/**
 * Whether a replay failure is permanent (drop loudly) or transient (back off and retry).
 *
 * Permanent: client-side Zod request rejection, backend 400 (ValidationPipe — deterministic
 * for the same payload), and 409/422. The backend's idempotent paths return SUCCESS on a
 * benign duplicate re-send (sales since #425/#427; transactions/supplies/cashbox/suppliers
 * in this change), so a conflict that still surfaces as an error names a constraint this
 * payload will hit on every future attempt.
 *
 * Transient: everything that can change out from under the payload — 404 (endpoint not deployed
 * yet — heals on backend deploy), 408/429, all 5xx, and network/timeout errors. "Transient" is not
 * "consumes an attempt": most of these are held without one by their own predicate (dead host,
 * route 404, transport, pause, and since ATL-1204 every other 5xx and axios's own timeout — see
 * `isServerFaultFailure`); what is left in the attempt-consuming bucket is 408/429, an entity-absent
 * 404 on a non-delete, any other non-refusal 4xx or non-2xx status (`http-other`), and errors with
 * no status that are not a timeout (the bulk stock mirror's per-item failure among them).
 *
 * ATL-908: 401/403 used to be listed here as transient "heals on migrate-on-demand". They are
 * neither permanent nor ordinarily transient, and that middle ground is what made them the
 * largest avoidable drop class in the fleet — see `isAuthorizationFailure`. They are now held
 * without consuming an attempt, like a dead host or an offline device.
 */
export function isPermanentReplayFailure(error: unknown): boolean {
  const name = (error as { name?: unknown })?.name
  if (name === 'ApiValidationError' && (error as { type?: unknown }).type === 'request') {
    return true
  }
  const status = replayFailureStatus(error)
  return status === 400 || status === 409 || status === 422
}

/**
 * ATL-902 — the backend HOST is gone, as opposed to the route being wrong.
 *
 * Bundles published before the Railway→Cloud Run move (ATL-744/749, app URL corrected
 * 2026-07-22) have `https://atlas-backend-production-d3df.up.railway.app/api` baked in at build
 * time. Railway answers every path on a deleted app with its OWN 404 page,
 * `{"message":"Application not found"}` — so the dual-write leg fails on every single call, for
 * every endpoint, forever, on that device. Measured: 1,354 events / 47 users / 27 orgs in 90d,
 * including ~50 `*.delete`/`softDelete` writes that therefore committed to Firestore and NOTHING
 * else, leaving Supabase holding rows the customer deleted.
 *
 * This has to be its own class rather than "a 404", because the two 404s mean opposite things:
 *
 *   • any OTHER 404 from the REAL backend — an entity that is genuinely absent, or a route that
 *     is not deployed yet — is transient and self-heals, which is why 404 is retryable above and
 *     must STAY retryable. (ATL-1194 split those two apart: they are distinguishable by body and
 *     they mean opposite things to a DELETE. See `isRouteNotFoundFailure` and
 *     `isEntityAbsentFailure`. This sentence used to name them as one, which is the conflation
 *     that ticket removed.);
 *   • a host 404 is environmental and permanent for as long as that bundle runs. Retrying cannot
 *     succeed, and it burned the entry's 10 attempts inside ~10 hours and then dropped it.
 *
 * Matched on the body text and not on the hostname, deliberately: the client cannot see which
 * base URL an old bundle was built with (that is the whole problem), but it can see that the
 * thing answering is not our API. Narrow by construction — our NestJS backend never emits this
 * message; every 404 it produces names the entity that was not found.
 */
export function isDeadBackendFailure(error: unknown): boolean {
  if (replayFailureStatus(error) !== 404) return false
  return /application not found/i.test(serverBodyMessage(error))
}

/**
 * ATL-1188 — the 404 that means "there was never a row here", as opposed to any other 404.
 *
 * The delete paths dual-write AFTER Firestore has committed, against a Supabase that may simply
 * not hold the document: anything created before its org started dual-writing was never mirrored.
 * The backend answers 404, and that is the correct, expected, uninteresting outcome — the delete
 * has nothing to do. Treating it as a failure would queue a replay for a row that never existed,
 * burning the entry's ten attempts and then dropping it loudly, once per legacy document deleted.
 *
 * So this is the one failure a delete may swallow. It is a CLASSIFIER and not `status === 404`
 * because two other things wear the same status, and both must stay loud:
 *
 *  • the dead-backend 404 (ATL-902): a Railway-era bundle gets `{"message":"Application not
 *    found"}` from a deleted host on EVERY call. ATL-902 measured ~50 `*.delete`/`softDelete`
 *    writes lost exactly this way — Firestore dropped the row, Supabase kept it, and the device
 *    reported nothing. Excluded here, so it re-throws into its own class and stays queued across
 *    the device's next OTA;
 *  • the ROUTE 404 (ATL-1194): Nest's fallback handler, `Cannot DELETE /api/…`, i.e. no handler
 *    ran. Also excluded, so it re-throws and is queued. An earlier revision of this docblock said
 *    route 404s were "indistinguishable from an entity 404 at this seam" and swallowed them on
 *    purpose; they are distinguishable by body, and the tie-break's own premise ("it would 404
 *    again anyway") is true only of an entity 404 — see `isRouteNotFoundFailure`.
 *
 * Status is read through `replayFailureStatus`, i.e. the same extraction the rest of this module
 * classifies on. The three call sites this replaces each hand-rolled their own predicate and two
 * of them matched on `error.message.includes('404')` — a substring, which reads a 60s timeout on
 * `/api/transactions/abc404def` as "already absent" and silently discards the write.
 */
/**
 * ATL-1194 — a ROUTE 404: no handler ran, so nothing was done.
 *
 * Nest's fallback handler answers an unmatched path with `NotFoundException('Cannot <METHOD>
 * <url>')`. That is the third thing wearing status 404 at this seam, and it is the opposite of
 * entity absence: an entity 404 means the handler ran and found no row, a route 404 means the
 * request never reached one. Nothing was deleted, nothing was updated, and the write is simply
 * gone if it is swallowed.
 *
 * It is genuinely reachable, not hypothetical: a device pointed at a base URL whose `/api` prefix
 * or route shape it no longer matches (a bundle predating a route rename), a proxy in front of the
 * API serving a Nest 404 for a path it does not forward, or an endpoint that has not deployed yet.
 *
 * WHY THIS OVERTURNS ATL-1188's TIE-BREAK RATHER THAN EXTENDING IT. `isEntityAbsentFailure` used
 * to fold route 404s into entity absence, with the reasoning: "indistinguishable from an entity
 * 404 at this seam, and a delete replayed against a deployed endpoint would 404 again anyway, so
 * the only thing the queue could add is noise." Both halves are wrong once the bodies are read:
 *
 *  • they ARE distinguishable. Of the 61 distinct `NotFoundException` messages in the backend,
 *    not one begins `Cannot <verb> /` — they name the entity (`Sale not found: <id>`) or, in one
 *    case, the failure (`Failed to fetch stores`); only Nest's fallback produces this shape. So
 *    the claim that carries the weight is the narrow one — no HANDLER 404 collides with the
 *    pattern — not the broader "every 404 names an entity". Same argument `isDeadBackendFailure`
 *    already rests on, applied to the other non-entity 404.
 *  • "it would 404 again anyway" is true of an ENTITY 404 and false of a route one. A route 404
 *    is transient by definition — the route appears and the replay succeeds. `isPermanentReplayFailure`
 *    has always said so by leaving 404 retryable, and the docblock above says it in words
 *    ("transient and self-heals on the next deploy"). The live path was drawing the opposite
 *    conclusion from the identical error.
 *
 * Matched on the body and not on the status, deliberately narrow. Nest itself keeps no method
 * list — `routes-resolver` interpolates `req.method` verbatim — so the alternation here is
 * exhaustive over the methods THIS CLIENT can send: `ReplayableRequestConfig.method` is typed
 * `GET|POST|PUT|PATCH|DELETE`, and `head`/`options` are carried only so the predicate does not
 * become wrong if that type widens. Narrow is the point: widening it to `\w+` would let a
 * handler that phrases a refusal as `Cannot restore /…` be re-thrown as a missing route, which
 * is pinned as a test.
 *
 * A 404 from something that is neither our backend nor Railway — a CDN, a load balancer, a Cloud
 * Run "page not found" after a domain mapping is removed — has no readable body AT ALL by the time
 * a classifier sees it (`normalizeApiErrorBody` substitutes axios's own `"Request failed with
 * status code 404"`). That third non-entity 404 is NOT matched here by body; it is excluded by
 * `isEntityAbsentFailure`'s positive signal instead (ATL-1199).
 */
export function isRouteNotFoundFailure(error: unknown): boolean {
  if (replayFailureStatus(error) !== 404) return false
  return /^\s*cannot\s+(get|head|post|put|patch|delete|options)\s+\//i.test(
    serverBodyMessage(error),
  )
}

export function isEntityAbsentFailure(error: unknown): boolean {
  return (
    replayFailureStatus(error) === 404 &&
    isBackendEnvelope404(error) &&
    !isDeadBackendFailure(error) &&
    !isRouteNotFoundFailure(error)
  )
}

/**
 * ATL-1199 — the POSITIVE signal that OUR backend answered a 404: the `AllExceptionsFilter` envelope
 * (`{ statusCode: 404, timestamp, path, message }`, `apps/atlas-backend/src/common/filters/
 * http-exception.filter.ts`), which every dual-write endpoint's error passes through.
 *
 * Absence used to be the default here: any 404 that was not the dead host or a route 404 read as
 * "the row was never mirrored", was swallowed, and — being a delete success — purged the document's
 * queued entries. A 404 with no readable body (a CDN, a load balancer, a Cloud Run "page not found",
 * a captive portal) reached exactly that branch, because `normalizeApiErrorBody` replaces an
 * unparseable body with axios's own message and `ApiRequestError` keeps no `.response`: the
 * ATL-1194 defect, surviving for the next infrastructure 404. The v2 outbox (`outboxClassify.ts`)
 * already refused that default; v1 now does too, through this one definition.
 *
 * Inverted: a 404 is entity absence only when the envelope proves a handler of ours looked.
 * Everything else re-throws and is classified and queued like any transient failure — the safe
 * direction, because a wrongly-queued delete replays once against an absent row and is removed
 * by the drain as delivered (ATL-1193)
 * whereas a wrongly-swallowed one loses the delete AND purges the queue.
 *
 * Both error shapes are read: the live path's `ApiRequestError.error` (the normalised `ApiError`,
 * which keeps `statusCode`/`timestamp`/`path` from either parse branch) and the replay path's raw
 * axios `response.data`. The Railway dead-host body (`{"message":"Application not found"}`) has
 * neither `timestamp` nor `path`.
 */
export function isBackendEnvelope404(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as Record<string, unknown>
  for (const body of [e.error, (e.response as Record<string, unknown> | undefined)?.data]) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) continue
    const b = body as Record<string, unknown>
    if (b.statusCode === 404 && typeof b.timestamp === 'string' && typeof b.path === 'string') {
      return true
    }
  }
  return false
}

/**
 * ATL-908 — WHY the server refused, not just that it did.
 *
 * `errorMessage(error)` on an axios rejection yields `"Request failed with status code 400"`, which
 * names the status and nothing else. That is what 78 permanent `savedCarts.create` drops were
 * reported with between 2026-08-01 and 2026-08-15: every one loud, fingerprinted and useless,
 * because a permanent refusal is only actionable if you can see WHAT was refused. A NestJS
 * ValidationPipe 400 carries the reason in its body (`message` is an array of field errors) and we
 * were throwing it away at the one moment it mattered.
 *
 * Returns '' when the body carries nothing useful, so the caller can omit the field rather than
 * record an empty string that reads like "the server said nothing".
 *
 * ⚠️ It does NOT read the AllExceptionsFilter envelope, and both its call sites are on the REPLAY
 * path — whose errors come from `typedClient.replayRequest`, which deliberately does not wrap in
 * `ApiRequestError`, so they are raw axios rejections whose `data.message` is the nested OBJECT
 * `{ message, error, statusCode }`. It therefore falls through to '' and a replayed refusal is
 * dropped with the bare `"Request failed with status code 4xx"`. That is a real, live ATL-908
 * residue, it is already filed as **ATL-1189**, and `dualWriteOutbox.test.js` pins today's
 * behaviour on purpose so that fixing it fails there and the pin is updated deliberately. It is
 * NOT fixed here: ATL-1194 owns the delete 404 classification, and `serverBodyMessage` below
 * carries the same one-level unwrap for that — the two functions stay separate because this one
 * must also join a `string[]`, which that one must not.
 */
function serverRefusalDetail(error: unknown): string {
  if (!error || typeof error !== 'object') return ''
  const e = error as Record<string, unknown>
  for (const body of [e.error, (e.response as Record<string, unknown> | undefined)?.data]) {
    if (typeof body === 'string' && body.trim()) return body.slice(0, 500)
    if (body && typeof body === 'object') {
      const message = (body as Record<string, unknown>).message
      // ValidationPipe sends an ARRAY of per-field messages; a plain handler sends a string.
      if (Array.isArray(message)) {
        const joined = message.filter((m) => typeof m === 'string').join('; ')
        if (joined) return joined.slice(0, 500)
      }
      if (typeof message === 'string' && message.trim()) return message.slice(0, 500)
    }
  }
  return ''
}

/**
 * The server-supplied message, across both client shapes (ApiRequestError and raw axios).
 *
 * ATL-1194 — it also unwraps ONE level of nesting, because the raw-axios shape is nested and the
 * typed one is not. `AllExceptionsFilter` responds `{ statusCode, timestamp, path, message }`
 * where `message` is the HttpException's own body, i.e. `{ message, error, statusCode }`. The
 * typed client flattens that in `normalizeApiErrorBody` before it ever reaches here; a caller on
 * the raw axios path does not, so `body.message` is an OBJECT and the pre-ATL-1194 version
 * returned '' for it — a Nest 404 read as "the server said nothing".
 *
 * In practice that blinded nothing YET: the only body-matching classifier before ATL-1194 was
 * `isDeadBackendFailure`, and Railway's body is flat. It matters now because
 * `isRouteNotFoundFailure` matches a body that IS nested, and because the replay path — whose
 * errors come from `typedClient.replayRequest` and are therefore never `ApiRequestError` — is
 * entirely raw. So this widens `isDeadBackendFailure` only in the direction of seeing more of
 * what the server actually said, and makes the route classifier work on both paths.
 */
function serverBodyMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return ''
  const e = error as Record<string, unknown>
  const bodies = [e.error, (e.response as Record<string, unknown> | undefined)?.data]
  for (const body of bodies) {
    if (typeof body === 'string') return body
    if (body && typeof body === 'object') {
      const message = (body as Record<string, unknown>).message
      if (typeof message === 'string') return message
      // The AllExceptionsFilter envelope: the real message is one level in.
      if (message && typeof message === 'object' && !Array.isArray(message)) {
        const inner = (message as Record<string, unknown>).message
        if (typeof inner === 'string') return inner
      }
    }
  }
  return ''
}

/**
 * ATL-1204 — what a server-fault hold persists as `lastError`: the status, the backend's `code` when
 * it sent one, and the server's own words, capped at 500 characters.
 *
 * The body alone is not enough. An expired hold is reported from this field only (`reportDrop` has
 * no error then) and `lastErrorClass` is `server-error` for every 5xx, so a body without the status
 * leaves a Nest `Internal server error` (500), a proxy page (502), a `write_master_undetermined`
 * (503) and Cloud Run's plain-text `upstream request timeout` (504) to be told apart by prose — the
 * last of which reads like the client's own timeout. Several backend 503s carry their identity only
 * in `code` (`write_master_undetermined`, `books_write_state_unknown`), not in the message text.
 */
function serverFaultDetail(error: unknown): string {
  const status = replayFailureStatus(error)
  const code = refusalCode(error)
  const prefix =
    status === undefined ? '' : `status ${status}${code !== UNCODED_REFUSAL ? ` ${code}` : ''}: `
  return (prefix + (serverBodyMessage(error) || errorMessage(error))).slice(0, 500)
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  try {
    return String(error)
  } catch {
    return 'unknown error'
  }
}

function utf8Bytes(s: string): number {
  try {
    return new TextEncoder().encode(s).length
  } catch {
    // No TextEncoder in this runtime — upper-bound by UTF-16 units × 2.
    return s.length * 2
  }
}

// ============================================================================
// Preconditioned writes (ATL-1503)
// ============================================================================

/**
 * ATL-1503 — a write that carries a PRECONDITION: the state of the document it was made from
 * (`expectedPayment` on a supply payment update). The backend refuses it (409, permanent) unless that
 * state is the document's current one, so such writes form a CHAIN — each is valid only after the one
 * before it — and the three shortcuts this queue takes with ordinary writes break the chain:
 *
 *   1. replacement: a newer same-label entry used to REPLACE the queued one ("the newer payload wins").
 *      For a chain that deletes a link: pay(tx-1) queued, then a method edit made from paid(tx-1)
 *      replaces it — the edit replays against an unpaid supply and is refused; Supabase never pays.
 *   2. purge: a live same-label success used to PURGE the queued one, for the same reason, with the
 *      same effect.
 *   3. overtaking: a live write was sent while older writes for its document were still queued. An
 *      unpay queued, then a live re-pay (made from "unpaid") reaches a supply that is still paid and
 *      is refused; then the unpay replays — Supabase ends unpaid with no leg, Firestore paid(tx-2).
 *
 * So a preconditioned entry is never replaced and never purged by a later write of its document, and
 * a preconditioned live write whose document still has queued entries is queued BEHIND them
 * (`bestEffortDualWrite`'s `queueBehindDocument`). One exception, deliberately kept: a DELETE of the
 * document (queued or landed) still supersedes everything queued for it. Nothing a preconditioned
 * update could do survives the delete (its replay is refused as "not found"), and dropping it there is
 * the existing rule-3 behaviour, not a loss.
 *
 * Scoped to preconditioned writes on purpose: "a live write overtakes the queue" is general, and the
 * general fix is ATL-1575.
 */
export function isPreconditionedRequest(request: ReplayableRequestConfig | undefined): boolean {
  const data = request?.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false
  const expected = (data as Record<string, unknown>).expectedPayment
  return expected !== undefined && expected !== null
}

const isPreconditionedEntry = (entry: OutboxEntry): boolean => isPreconditionedRequest(entry.request)

/**
 * ATL-1503 — does the queue hold any entry for this document (this org's, or any when unknown)?
 *
 * READ-ONLY, and deliberately outside `withQueue`: it runs before every supply payment save, and
 * `withQueue` both takes the uncapped cross-tab Web Lock and rewrites the queue to storage. A probe
 * must do neither — a lock held by a frozen tab would hang the save, and a probe has nothing to
 * write. It can read a queue another tab is mid-way through rewriting; the effect is sending one write
 * live that could have queued, and what that live write can meet — refused, retried after its pay, or
 * (an unpay of a leg-less payment) accepted out of order — is listed on `queuedBehindDocument` in
 * dualWrite.ts. A corrupt queue reads as empty here and is reported and reset by the next
 * read-modify-write, as before.
 */
export async function outboxHoldsDocument(firestoreId: string, orgFirestoreId?: string): Promise<boolean> {
  let queue: OutboxEntry[]
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    queue = Array.isArray(parsed) ? parsed.filter(isOutboxEntry) : []
  } catch {
    return false
  }
  return queue.some(
    (e) => e.firestoreId === firestoreId && (!orgFirestoreId || e.orgFirestoreId === orgFirestoreId),
  )
}

// ============================================================================
// Enqueue / supersede
// ============================================================================

function newEntryId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function reportNotCapturable(label: string, reason: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    category: 'dual_write.outbox',
    level: 'warning',
    message: `outbox: ${label} failure not capturable (${reason})`,
    data: { label, reason, ...(data || {}) },
  })
}

/**
 * Queue a failed dual-write for durable retry. Returns true when the entry was queued.
 *
 * Callers (bestEffortDualWrite) must only pass TRANSIENT failures — deterministic defects
 * never enter the queue. The failing request is taken from `error.requestConfig`, which the
 * api-client attaches to every error that represents a write that reached the transport.
 * Refused (loudly, via breadcrumb — the failure event itself carries
 * `dual_write_outbox_queued:false`) when: no replayable request, no derivable document id
 * (replay would not be idempotent — rule 1), no org, or the entry/queue exceeds size caps.
 */
export async function enqueueDualWriteFailure(
  label: string,
  error: unknown,
  context?: {
    firestoreId?: string
    orgFirestoreId?: string
    fromReplay?: boolean
    /** ATL-1207 — the Firestore location this write deleted the entity FROM. See `OutboxEntry`. */
    deletedFrom?: DeletedEntityLocation
  },
): Promise<boolean> {
  const request = (error as { requestConfig?: ReplayableRequestConfig })?.requestConfig
  return queueEntry(label, request, context, {
    lastError: errorMessage(error),
    lastErrorClass: replayFailureClass(error),
    nextAttemptAt: Date.now() + BASE_BACKOFF_MS,
  })
}

/**
 * ATL-1503 — queue a write that was NOT sent, behind the entries its document already has (see
 * `isPreconditionedRequest`). It never failed, so it is due at once: the drain replays a document's
 * entries oldest-first and it waits for the older ones exactly as a failed write would.
 */
export async function enqueueBehindQueuedEntries(
  label: string,
  request: ReplayableRequestConfig,
  context: { firestoreId: string; orgFirestoreId?: string },
  options?: {
    /**
     * Asked under the queue lock, immediately before the entry is added. A caller that stopped
     * waiting (its cap expired, so it sent the write live) answers false and nothing is queued —
     * otherwise the same write would ALSO replay later.
     */
    stillWanted?: () => boolean
  },
): Promise<boolean> {
  return queueEntry(
    label,
    request,
    context,
    {
      lastError: 'queued behind earlier writes of this document (ATL-1503), not sent live',
      nextAttemptAt: Date.now(),
    },
    options?.stillWanted,
  )
}

/**
 * ATL-1459 — queue the mirror of a Firestore write whose JS continuation died before its dual-write
 * ran (the app was killed or reloaded between `batch.commit()` and the mirror). The request was
 * persisted BEFORE the commit by `mirrorIntent.ts`, and its Firestore document has since been
 * witnessed to exist, so this is exactly the entry the live path would have queued had it run and
 * failed: the same request, keyed on the same document, under every rule of this queue (rule 3's
 * supersede leaves an ATL-1503 preconditioned entry alone; the drain applies the gate, the order and
 * the drop rules). It never failed, so it is due at once.
 *
 * `enqueuedAt` is the time the WRITE was made, not the time it was recovered: the entry's age is the
 * write's age (MAX_AGE_MS expires it on the same clock as every other entry), and within its
 * document the drain orders by it, so a create recovered late still replays before a later write of
 * the same document that was queued first.
 */
export async function enqueueRecoveredMirrorIntent(
  label: string,
  request: ReplayableRequestConfig,
  context: { firestoreId: string; orgFirestoreId: string; enqueuedAt: number },
): Promise<boolean> {
  return queueEntry(
    label,
    request,
    { firestoreId: context.firestoreId, orgFirestoreId: context.orgFirestoreId },
    {
      lastError: 'recovered mirror intent (ATL-1459): the app died after the Firestore commit, before the mirror ran',
      nextAttemptAt: Date.now(),
      enqueuedAt: context.enqueuedAt,
    },
  )
}

/**
 * ATL-1445 stage 3, P8 — queue a LIVE mirror that backend PR B1 refused because the organization is
 * Supabase-master (409 `WRITE_MASTER_SUPABASE`, `global/Write/mirrorRefusalConversion.ts`). It is due at
 * once and the caller kicks a `master-refusal` drain, which re-sends it, meets the same refusal and
 * converts it into outbox v2 under this entry's id. It goes through THIS queue rather than straight
 * into v2 so that the drain — which replays a document's entries in order — is the only converter: a
 * live refusal can then never overtake an older write of the same document that is queued or in flight
 * here. Every rule of this queue applies (supersede, purge, the size caps). Never called while no
 * organization is Supabase-master.
 */
export async function enqueueRefusedMirror(
  label: string,
  request: ReplayableRequestConfig,
  context: { firestoreId: string; orgFirestoreId: string },
): Promise<boolean> {
  return queueEntry(label, request, context, {
    lastError: 'refused as a Firestore-first mirror of a Supabase-master organization (409 WRITE_MASTER_SUPABASE); converted into outbox v2 on its replay',
    nextAttemptAt: Date.now(),
  })
}

async function queueEntry(
  label: string,
  request: ReplayableRequestConfig | undefined,
  context:
    | {
        firestoreId?: string
        orgFirestoreId?: string
        fromReplay?: boolean
        deletedFrom?: DeletedEntityLocation
      }
    | undefined,
  timing: {
    lastError: string
    lastErrorClass?: ReplayFailureClass
    nextAttemptAt: number
    enqueuedAt?: number
  },
  stillWanted?: () => boolean,
): Promise<boolean> {
  if (!request || request.method === 'GET') {
    reportNotCapturable(label, 'no-replayable-request', { hasRequest: Boolean(request) })
    return false
  }

  // Rule 10 (ATL-1951): never persist a credential write — not the password, not a late replay of it.
  if (isCredentialWrite(label, request)) {
    reportNotCapturable(label, 'credential-write')
    return false
  }

  const firestoreId = deriveDocumentId(request, context?.firestoreId)
  if (!firestoreId) {
    // Rule 1: without a document id the replay has no idempotency key and no supersede key —
    // a replay whose original actually committed server-side would DOUBLE-APPLY. Refuse.
    reportNotCapturable(label, 'no-document-identity')
    return false
  }

  const orgFirestoreId = context?.orgFirestoreId
  if (!orgFirestoreId) {
    // Without an org the entry would replay under whichever account is active later.
    reportNotCapturable(label, 'no-organization')
    return false
  }

  const entry: OutboxEntry = {
    id: newEntryId(),
    label,
    request,
    firestoreId,
    orgFirestoreId,
    enqueuedAt: timing.enqueuedAt ?? Date.now(),
    attempts: 0,
    nextAttemptAt: timing.nextAttemptAt,
    lastError: timing.lastError,
    ...(timing.lastErrorClass ? { lastErrorClass: timing.lastErrorClass } : {}),
    // ATL-1207: the delete's precondition, persisted with it. It is NOT validated here — the probe
    // is the single validator, and it answers `unknown` for anything it cannot read, which holds
    // the entry rather than replaying it. Validating in two places would give the same malformed
    // location two different answers (queued unguarded here, held there), and only one of them can
    // be the safe one.
    ...(context?.deletedFrom ? { deletedFrom: context.deletedFrom } : {}),
  }

  let entryBytes = 0
  try {
    entryBytes = utf8Bytes(JSON.stringify(entry))
  } catch {
    entryBytes = Number.MAX_SAFE_INTEGER // unserializable payload can never be persisted
  }
  if (entryBytes > MAX_ENTRY_BYTES) {
    reportDrop(entry, 'entry-too-large')
    flushDropTally('enqueue')
    return false
  }

  const survived = await withQueue(async (queue) => {
    if (stillWanted && !stillWanted()) return { queue, result: false }
    // Supersede: a NEWER failed write for the same (label, document) replaces the older
    // queued one — replaying both would apply the stale one last-write-wins over the fresh.
    //
    // ATL-1194 — a QUEUED delete supersedes the document across ALL labels, exactly as a LANDED
    // one does in `noteDualWriteSuccess`, and by the identical argument: these paths dual-write
    // AFTER Firestore has committed the delete, so every earlier queued write for that document
    // is stale by construction. The rule was applied only on success, and that asymmetry was a
    // resurrection path this ticket opened rather than closed.
    //
    // Measured, driving the real `deleteSale` over the real queue: `sales.create` queued on a
    // 503; the cashier voids the sale; the Supabase DELETE route-404s and is now queued (this
    // ticket's fix). The drain replays a document's entries OLDEST-FIRST, so the create goes
    // first — and against a backend that answers POST but not DELETE it LANDS, re-inserting the
    // sale the cashier deleted, while the delete is held and finally expires at MAX_AGE_MS. Net:
    // Supabase permanently holds a voided sale, which is this ticket's own title.
    //
    // Before ATL-1194 that pair could not arise, by accident: the route 404 resolved as a success
    // and `noteDualWriteSuccess` purged the create. Queueing the delete is right; keeping a stale
    // create alongside it is not. Applying the purge here rather than in the wrapper keeps it in
    // the same queue transaction as the insert, so no drain can interleave between them.
    //
    // RESIDUAL, named rather than left to be found: a delete that neither lands nor queues — one
    // refused by `reportNotCapturable`, or skipped by an informed gate deny — still leaves the
    // document's earlier entries in place. Both are already loud, and neither is reached by the
    // failure this comment is about.
    // ATL-1192 round-3 review: `fromReplay` withdraws this rule for a delete replayed out of the
    // in-session pending-skip queue, exactly as it does in `noteDualWriteSuccess` — and for the
    // identical reason, which is the whole argument for the rule in the first place. It rests on
    // the delete being NEWS ("these paths dual-write AFTER Firestore has committed the delete, so
    // every earlier queued write for that document is stale by construction"). A replayed delete
    // is the opposite: an OLD statement being flushed, so an entry queued after it is NEWER.
    //
    // Without this the failure path lost what the success path had just been fixed to keep, and
    // lost more of it: `delete docA` gate-denied → `create docA` fails transiently and is queued
    // durably → the old delete replays, ALSO fails transiently, and its enqueue purged the create.
    // The recreated row is gone from Supabase AND a stale delete now sits in the queue for seven
    // days ready to remove it again. Measured through the real queue before this line existed.
    const supersedesWholeDocument = isDeleteLabel(entry.label) && !context?.fromReplay
    // ATL-1503: a preconditioned entry replaces NOTHING — not even an older entry without a
    // precondition (a pre-OTA bundle's): it was made from the state that older write leaves, so it
    // queues behind it.
    const replacesNothing = isPreconditionedEntry(entry)
    let next = queue.filter((e) => {
      if (e.firestoreId !== entry.firestoreId) return true
      if (supersedesWholeDocument) return false
      if (replacesNothing) return true
      // ATL-1503: a preconditioned entry is a link of a chain, never "an older payload".
      if (isPreconditionedEntry(e)) return true
      return e.label !== entry.label
    })
    if (supersedesWholeDocument && next.length !== queue.length) {
      Sentry.addBreadcrumb({
        category: 'dual_write.outbox',
        level: 'info',
        message: `outbox: queued ${entry.label} superseded ${queue.length - next.length} earlier entr(y/ies) for ${entry.firestoreId}`,
        data: { label: entry.label, firestoreId: entry.firestoreId, superseded: queue.length - next.length },
      })
    }
    next = [...next, entry]

    // Overflow: drop the oldest DOCUMENT GROUP (all its labels together — dropping a create
    // while keeping its update would leave an update that can only 404-loop), loudly.
    const overCount = () => next.length > MAX_ENTRIES
    const overBytes = () => {
      try {
        return utf8Bytes(JSON.stringify(next)) > MAX_TOTAL_BYTES
      } catch {
        return false
      }
    }
    while ((overCount() || overBytes()) && next.length > 1) {
      const oldest = next.reduce((a, b) => (a.enqueuedAt <= b.enqueuedAt ? a : b))
      const doomed = next.filter((e) => e.firestoreId === oldest.firestoreId)
      next = next.filter((e) => e.firestoreId !== oldest.firestoreId)
      for (const d of doomed) reportDrop(d, 'queue-overflow')
    }
    flushDropTally('enqueue')
    // Overflow can legally evict the just-added entry's own group (it may BE the oldest);
    // the caller's `dual_write_outbox_queued` tag must not claim durability that was
    // immediately revoked.
    return { queue: next, result: next.some((e) => e.id === entry.id) }
  })
  if (!survived) return false

  Sentry.addBreadcrumb({
    category: 'dual_write.outbox',
    level: 'info',
    message: `outbox: queued ${label} for retry`,
    data: { label, firestoreId: entry.firestoreId, entryId: entry.id },
  })
  // eslint-disable-next-line no-console
  console.log(
    `[dualWriteOutbox] queued ${label} for durable retry`,
    JSON.stringify({ label, firestoreId: entry.firestoreId, entryId: entry.id }),
  )

  ensureDrainInterval()
  return true
}

/**
 * A dual-write for this document succeeded — purge queued entries the success makes stale, so a
 * later drain cannot regress the row (rule 3):
 *  - any success purges the document's SAME-LABEL entries (the queued payload is older);
 *  - a DELETE success purges the document's entries across ALL labels — replaying a queued
 *    create/update after the document was deleted would resurrect it in Supabase.
 *
 * `fromReplay` withdraws the second rule, and only the second (ATL-1192, round-2 review finding
 * B). The cross-label purge rests on the delete being NEWS: it just happened, so everything queued
 * for that document predates it. A delete replayed out of the pending-skip queue is the opposite —
 * an OLD statement being flushed, and entries queued after it are NEWER. Without this flag the
 * sequence `delete docA gate-denied` → `create docA fails transiently and is queued durably` →
 * `the old delete finally replays` purged the create, losing the recreated row from Supabase with
 * nothing left anywhere to replay it. The same-label purge stays correct either way: that entry is
 * the same statement about the same document.
 */
export async function noteDualWriteSuccess(
  label: string,
  context?: { firestoreId?: string; alternateIds?: string[]; fromReplay?: boolean },
): Promise<void> {
  // Purge by EVERY id the success is known under: the caller's context id plus any ids from
  // the API response. Some endpoints key their URL on one id and their document on another
  // (e.g. saleSettings: URL storeId vs settings doc id) — purging on the union means a queued
  // stale write can't dodge the purge by having been keyed on the other spelling.
  const ids = new Set(
    [context?.firestoreId, ...(context?.alternateIds || [])].filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    ),
  )
  if (ids.size === 0) return
  // ATL-1207 — recorded BEFORE the queue filter below, and independently of it: a live write can
  // supersede a queued delete that the running drain has already read past. A REPLAY is excluded
  // deliberately, on the same grounds as the pending-skip queue's `isPendingSkipReplay`: a replay
  // is by construction OLDER than everything queued after it, so reading it as "this document
  // exists now" would let a replayed create withdraw the delete that followed it.
  if (liveWritesDuringDrain && !context?.fromReplay) {
    for (const id of ids) liveWritesDuringDrain.add(id)
  }
  const isDelete = isDeleteLabel(label) && !context?.fromReplay
  await withQueue(async (queue) => {
    if (queue.length === 0) return { queue, result: undefined }
    const next = queue.filter((e) => {
      if (!ids.has(e.firestoreId)) return true
      if (isDelete) return false
      // ATL-1503: a landed write does not make a queued preconditioned one stale — it is the link
      // BEFORE it that the chain still needs (see `isPreconditionedRequest`).
      if (isPreconditionedEntry(e)) return true
      return e.label !== label
    })
    if (next.length !== queue.length) {
      const idList = [...ids].join(',')
      Sentry.addBreadcrumb({
        category: 'dual_write.outbox',
        level: 'info',
        message: `outbox: purged ${queue.length - next.length} superseded entr(y/ies) for ${idList} (live ${label} landed)`,
        data: { label, firestoreId: idList, purged: queue.length - next.length },
      })
    }
    return { queue: next, result: undefined }
  })
}

// ============================================================================
// Drain
// ============================================================================

/**
 * Documents a LIVE dual-write landed for while a drain pass is running (ATL-1207).
 *
 * The recreate probe answers from a server read taken BEFORE the request it authorises goes out,
 * so a live write landing inside that window is invisible to the verdict — and rule 3's purge does
 * not cover it either, because a live create purges only its same-label entries. Non-null ONLY
 * while `drainNow` is between its group loop's start and end, so nothing accumulates between
 * passes; replays never land in it (they are recorded with `fromReplay`, and the outbox's own
 * replays do not go through `dualWrite` at all).
 */
let liveWritesDuringDrain: Set<string> | null = null

let drainInFlight: Promise<void> | null = null
let lastDrainAt = 0
let lastStallReportAt = 0
let drainInterval: ReturnType<typeof setInterval> | null = null

function ensureDrainInterval(): void {
  if (drainInterval) return
  drainInterval = setInterval(() => {
    drainDualWriteOutbox('interval').catch(() => undefined)
  }, DRAIN_INTERVAL_MS)
}

function stopDrainInterval(): void {
  if (!drainInterval) return
  clearInterval(drainInterval)
  drainInterval = null
}

/** TEST-ONLY: clear module state (interval, throttle, in-flight marker, autostart). */
export function resetOutboxForTests(): void {
  stopDrainInterval()
  stopDualWriteOutboxAutoDrain()
  drainInFlight = null
  lastDrainAt = 0
  lastStallReportAt = 0
  liveWritesDuringDrain = null
  queueMutex = Promise.resolve()
  exclusiveRequests = 0
  dropTallies.drain = null
  dropTallies.enqueue = null
}

// ============================================================================
// ATL-1445 stage 3, P8 — exclusive access for the v1 → v2 migration
// ============================================================================

/**
 * How long `withExclusiveV1Queue` waits to hold the v1 drain and the queue before giving up (review 1,
 * S-4). A running drain stops taking new entries once a migration asks (`exclusiveRequests`), so the wait
 * is ONE replay in flight: the api-client write timeout (60 s, `typedApiClient.ts`) plus the recreate
 * probe before it and the queue writes after it. 90 s covers that; past it something is wedged (a frozen
 * web tab holding a lock) and the migration reports `busy` rather than hold the session's writes longer.
 */
export const V1_EXCLUSIVE_WAIT_MS = 90_000

/**
 * Migrations waiting for, or holding, the v1 queue (`withExclusiveV1Queue`). While non-zero, a running
 * drain finishes the entry it is sending and takes no more (`deferredToMigration`), so the wait is
 * bounded by one replay. Zero for every session today.
 */
let exclusiveRequests = 0

/** The v1 queue could not be held within the wait: nothing was read and nothing changed. */
export class V1OutboxBusyError extends Error {
  constructor(public readonly waitMs: number) {
    super(`the v1 dual-write outbox could not be held within ${waitMs} ms (a drain or another tab holds it)`)
    this.name = 'V1OutboxBusyError'
  }
}

/** Settles when `promise` settles or after `ms`, whichever is first; never rejects, leaves no timer. */
async function settledOrAfter(promise: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      promise.then(
        () => undefined,
        () => undefined,
      ),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * Run `fn` over the v1 queue while NOTHING ELSE can touch it (ATL-1445 stage 3, P8: the v1 → v2
 * migration at the flip, `global/Write/v1Migration.ts`). Never called for an organization today.
 *
 * What "nothing else" means, lock by lock, and why each is needed:
 *  1. THE DRAIN, in this JS context: `drainInFlight` is the drain's single-flight slot. A running drain
 *     is waited out, then the slot is TAKEN, so a drain requested meanwhile joins this work instead of
 *     starting (exactly as a drain requested during a drain does). An entry the drain is sending is
 *     therefore never also copied — the migration sees it only after its send has returned and its
 *     outcome (delivered, failed, converted) is recorded.
 *  2. THE DRAIN, across web tabs: the drain Web Lock, WAITED for (not `ifAvailable`), so another tab's
 *     drain finishes first and no tab starts one until this returns (theirs take it `ifAvailable`).
 *  3. THE QUEUE: the same in-process mutex and queue Web Lock as every read-modify-write (`withQueue`),
 *     held for the whole of `fn`, so a live enqueue, supersede or purge cannot interleave with the copy.
 * Same order as a drain takes them (drain, then queue), so the two cannot deadlock.
 *
 * `fn` returns the queue to save, or `null` to save nothing (a migration with nothing to move must not
 * rewrite the queue).
 *
 * Bounded: if the locks are not all held within `waitMs` the call rejects with `V1OutboxBusyError`
 * having read and changed nothing — and if they are granted later, the abandoned work does nothing.
 * Once `fn` has started it runs to completion.
 */
export async function withExclusiveV1Queue<T>(
  fn: (queue: readonly OutboxEntry[]) => Promise<{ queue: OutboxEntry[] | null; result: T }>,
  options: { waitMs?: number } = {},
): Promise<T> {
  const waitMs = options.waitMs ?? V1_EXCLUSIVE_WAIT_MS
  const deadline = Date.now() + waitMs
  // 1. Ask a running drain to stop taking entries, wait it out, then take its slot in the same tick the
  //    loop sees it free.
  exclusiveRequests += 1
  try {
    while (drainInFlight) {
      const remaining = deadline - Date.now()
      if (remaining <= 0) throw new V1OutboxBusyError(waitMs)
      await settledOrAfter(drainInFlight, remaining)
    }
  } catch (error) {
    exclusiveRequests -= 1
    throw error
  }
  let releaseSlot: () => void = () => undefined
  const slot = new Promise<void>((resolve) => {
    releaseSlot = resolve
  })
  drainInFlight = slot

  let started = false
  let abandoned = false
  let timer: ReturnType<typeof setTimeout> | undefined
  /** Asked with every lock held: run, or (the wait expired) do nothing at all. */
  const mayStart = (): boolean => {
    if (abandoned) return false
    started = true
    if (timer !== undefined) clearTimeout(timer)
    return true
  }
  try {
    // 2 + 3. The drain lock (web), then the queue (mutex + queue lock), then `fn`.
    const work = (async (): Promise<{ ran: true; value: T } | { ran: false }> => {
      const locks = webLocks()
      if (!locks) return withQueueExclusively(fn, mayStart)
      return locks.request(`${WEB_LOCK_NAME}-drain`, {}, () => withQueueExclusively(fn, mayStart)) as Promise<
        { ran: true; value: T } | { ran: false }
      >
    })()
    const expired = new Promise<'expired'>((resolve) => {
      timer = setTimeout(
        () => {
          if (started) return
          abandoned = true
          resolve('expired')
        },
        Math.max(0, deadline - Date.now()),
      )
    })
    const outcome = await Promise.race([work, expired])
    if (outcome === 'expired' || !outcome.ran) throw new V1OutboxBusyError(waitMs)
    return outcome.value
  } finally {
    exclusiveRequests -= 1
    if (timer !== undefined) clearTimeout(timer)
    if (drainInFlight === slot) drainInFlight = null
    releaseSlot()
  }
}

/** `withQueue`'s locking, for `withExclusiveV1Queue`: asks `mayStart` under the locks; saves only a non-null queue. */
function withQueueExclusively<T>(
  fn: (queue: readonly OutboxEntry[]) => Promise<{ queue: OutboxEntry[] | null; result: T }>,
  mayStart: () => boolean,
): Promise<{ ran: true; value: T } | { ran: false }> {
  const rmw = async (): Promise<{ ran: true; value: T } | { ran: false }> => {
    if (!mayStart()) return { ran: false }
    const queue = await loadQueue()
    const { queue: next, result } = await fn(queue)
    if (next !== null) await saveQueue(next)
    return { ran: true, value: result }
  }
  const run = queueMutex.then(async () => {
    const locks = webLocks()
    if (locks) {
      return locks.request(WEB_LOCK_NAME, {}, rmw) as Promise<{ ran: true; value: T } | { ran: false }>
    }
    return rmw()
  })
  queueMutex = run.catch(() => undefined)
  return run
}

// ============================================================================
// Lifecycle (ATL-983) — the outbox drains because a queue EXISTS, not because a write happened
// ============================================================================

let autoDrainStarted = false
let unsubscribeOrg: (() => void) | null = null

/**
 * Start the outbox's own drain lifecycle, independently of the dual-write path.
 *
 * WHY THIS EXISTS: before it, every drain trigger required a dual-write in the current process.
 * `'launch'` is named for app start but lives INSIDE `bestEffortDualWrite`, after the gate's
 * `return null` — so a gate-denied session never kicked it. `'live-success'` needs a write that
 * succeeded. The 5-minute interval was armed only by a new enqueue. The net effect: a till that
 * queued writes on Friday and did not sell on Saturday never opened its queue, and the entries
 * aged out unattempted — 19 of 44 expired drops had `attempts` of 0 or 1 at 168–456 hours.
 *
 * Replaying a queued entry is not a new write and does not need the write path's permission: the
 * Firestore document it mirrors was committed long ago, and the entry was queued under a gate
 * that said yes. So this is wired to the two moments at which a replay can actually succeed —
 * process start, and the moment the session learns which organization it is — and to nothing
 * about whether a sale happens to be in flight.
 *
 * Total by construction: it is called at app boot and must never be able to prevent one.
 */
export function startDualWriteOutboxAutoDrain(): void {
  if (autoDrainStarted) return
  autoDrainStarted = true

  // Storage is readable immediately; the org usually is not. This pass is normally a no-op that
  // simply arms the tick, and it is cheap enough to be worth doing unconditionally.
  void drainDualWriteOutbox('app-start').catch(() => undefined)

  try {
    // Lazy requires for the same cycle reason as the drain's (see drainNow).
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getJotaiStore } = require('./types') as {
      getJotaiStore: () => {
        get: (atom: unknown) => unknown
        sub: (atom: unknown, listener: () => void) => () => void
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { organizationAtom } = require('../AtomsProvider') as { organizationAtom: unknown }

    const store = getJotaiStore()
    const readOrgId = (): string | undefined => {
      const org = store.get(organizationAtom) as { id?: unknown } | null | undefined
      return typeof org?.id === 'string' && org.id.length > 0 ? org.id : undefined
    }

    let lastOrgId = readOrgId()
    unsubscribeOrg = store.sub(organizationAtom, () => {
      const orgId = readOrgId()
      // Only a CHANGE of identity is a new opportunity: the atom is written on every org-shaped
      // update (settings, onboarding flags), and draining on each would be a pointless storage
      // read per keystroke.
      if (!orgId || orgId === lastOrgId) return
      lastOrgId = orgId
      void drainDualWriteOutbox('org-ready').catch(() => undefined)
    })
  } catch (error) {
    // A boot-time wiring failure would silently return this module to the broken behaviour, so
    // it is reported rather than swallowed. The `app-start` pass above and the idle tick still
    // run; only the org-ready edge is lost.
    // eslint-disable-next-line no-console
    console.error('[dualWriteOutbox] auto-drain could not subscribe to the organization', errorMessage(error))
    Sentry.captureMessage('dual-write outbox auto-drain could not subscribe to the organization', {
      level: 'error',
      tags: { dual_write_outbox: 'autodrain-subscribe-failed' },
      fingerprint: ['dual-write-outbox-autodrain-subscribe-failed'],
      contexts: { dual_write_outbox: { error: errorMessage(error) } },
    })
  }
}

/** Tear the lifecycle down (tests; and symmetry for any future teardown path). */
export function stopDualWriteOutboxAutoDrain(): void {
  autoDrainStarted = false
  if (unsubscribeOrg) {
    try {
      unsubscribeOrg()
    } catch {
      // A store that is already gone cannot be unsubscribed from; nothing to do.
    }
    unsubscribeOrg = null
  }
}

/**
 * Did this failure happen BEFORE any server saw the request? (ATL-911)
 *
 * Axios reports a request that got no response at all with `error.request` set and `error.response`
 * absent, and the message `Network Error`. That is the device having no connectivity — DNS,
 * radio, captive portal — and it says nothing whatsoever about the payload or the backend.
 *
 * DELIBERATELY NARROW. A TIMEOUT is excluded: `timeout of 60000ms exceeded` means the request WAS
 * delivered and the server did not answer in time, which can mean the server is struggling. Keeping
 * it out of this class keeps "we could not reach anything" and "the backend is in trouble" apart —
 * since ATL-1204 the second is its own hold (`isServerFaultFailure`), with its own doubling backoff
 * and its own counter, instead of consuming attempts.
 */
export function isTransportFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { message?: unknown; response?: unknown; request?: unknown; code?: unknown }
  if (e.response !== undefined) return false // a server answered — a status, classified below
  if (e.code === 'ECONNABORTED' || /timeout/i.test(String(e.message ?? ''))) return false
  return e.request !== undefined || /network error/i.test(String(e.message ?? ''))
}

/**
 * ATL-1805 — the organization's dual-write is PAUSED for a re-migration: nothing executed, and the
 * same request will be accepted once the pause lifts.
 *
 * Two senders, one lease (`organizations.dual_write_paused_until`): `MigrationPauseInterceptor`
 * (`503 migration_paused`, before any handler) and, for a books write that raced the lease being
 * armed, `assertBooksWriteMayExecute` (`503 books_write_paused`, before the first write). Both say
 * "not executed, retry after the pause". `NOT_EXECUTED_CODES` in `writeWBackendFirst.ts` lists both,
 * compared the same case-insensitive way, plus `books_write_state_unknown` — which is deliberately
 * NOT here: an unreadable lease proves nothing executed (outbox v2 may resend it) but it is a backend
 * fault, not a pause, so this queue holds it like any other 5xx (`isServerFaultFailure`, ATL-1204).
 *
 * Why it is held rather than counted: a fenced re-migration of a large organization runs for hours
 * (Justore's took over an hour and failed), and ten doubling attempts are spent in ~8.5h — a write
 * made at the start of a long run, or of two runs back to back, would be dropped as
 * `attempts-exhausted` for a refusal that said nothing about it. Before ATL-1805 the interceptor
 * answered a synthetic `200 { paused: true }` instead, which this queue took as DELIVERED.
 */
export function isMigrationPausedFailure(error: unknown): boolean {
  if (replayFailureStatus(error) !== 503) return false
  const code = refusalCode(error).toLowerCase()
  return code === 'migration_paused' || code === 'books_write_paused'
}

/**
 * ATL-1760 — the enum a failure is reported as. Ordered like the drain's own branches, so the
 * class names the branch that handled it: the three no-attempt holds first (dead host, route 404,
 * transport), then the statuses. A timeout is its own class even though the drain holds it with the
 * 5xx (`isServerFaultFailure`, ATL-1204): "never reached the API" versus "reached it and waited 60 s"
 * is the question an expired entry has to answer.
 */
export function replayFailureClass(error: unknown): ReplayFailureClass {
  if (isDeadBackendFailure(error)) return 'dead-backend'
  if (isRouteNotFoundFailure(error)) return 'route-404'
  if (isTransportFailure(error)) return 'transport'
  if (isMigrationPausedFailure(error)) return 'migration-paused'
  // The client's own schema refused the request before it was sent (`isPermanentReplayFailure`'s
  // first branch): no status, and not the same thing as an unknown failure.
  const name = (error as { name?: unknown })?.name
  if (name === 'ApiValidationError' && (error as { type?: unknown }).type === 'request') {
    return 'client-validation'
  }
  const status = replayFailureStatus(error)
  // ATL-1204: the same predicate the drain holds on, so `timeout` always names that branch — an
  // `Error` that merely QUOTES a timeout (the bulk stock mirror's per-item `statement timeout`) or
  // axios's `Request aborted` consumes an attempt, and is `other`.
  if (status === undefined) return isClientTimeoutFailure(error) ? 'timeout' : 'other'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not-found'
  if (status === 400 || status === 409 || status === 422) return 'refused'
  if (status >= 500) return 'server-error'
  return 'http-other'
}

/**
 * ATL-908 — the server refused the CALLER, not the payload.
 *
 * ATL-1137 corrects the status attribution this docblock used to assert. `FirebaseAuthGuard` can
 * never emit a 403, and that is STRUCTURAL rather than a property of the branches that happen to
 * exist today: its explicit refusals all throw `UnauthorizedException`, and its catch-all
 * re-throws only two classes as-is — `UnauthorizedException`, and (ATL-1198)
 * `OrganizationReadUnavailableError`, a plain `Error` the filter maps to 500. Everything else —
 * a `ForbiddenException` raised anywhere inside the guard body included — is converted into
 * `UnauthorizedException('Authentication failed')`. Neither re-thrown class can be a 403, so the
 * invariant survives ATL-1198 (pinned by `firebase-auth-status-split.atl1137.spec.ts`, which
 * asserts the ForbiddenException case directly). Line numbers are deliberately not cited: an
 * earlier version of this comment carried five of them and they were stale within a release.
 * So the two statuses have DIFFERENT senders, and the old "a 401/403 is returned by
 * FirebaseAuthGuard" was true only of the 401:
 *
 *   • 401 — `FirebaseAuthGuard`: no token on the request, a token it could not verify, a Firebase
 *     user with no (or an inactive) `users` row in Supabase, or — the fifth shape, and the one
 *     that never heals — a resolved organization whose Supabase row is soft-deleted
 *     (`Organization has been deleted`). Since ATL-1198 that string means what it says: a FAILED
 *     organization read is a 500 now, not this 401. See ATL-1204 for the durability consequence,
 *     which runs against the 5xx. The deleted-org shape is bounded by MAX_AGE_MS like the
 *     rest rather than special-cased: it is indistinguishable from the others at the client (the
 *     status is the same and the body text is not a contract), and being wrong about it costs a
 *     week of cheap probes, while being wrong the other way destroys a write.
 *   • 403 — `RolesGuard` / `PermissionsGuard`, or a service-level `ForbiddenException`: a verdict
 *     on the caller's ROLE. See `isForbiddenFailure`, which splits it out for REPORTING only.
 *
 * What both share, and the only property this predicate needs, is that the body was never
 * evaluated: the refusal says nothing about whether this write can ever succeed — exactly what
 * makes counting it as an "attempt" wrong.
 *
 * Why it cannot stay in the generic transient bucket, which is where it was: the things that
 * clear a 401 — the auth session restoring, or a human migrating the user/org (ATL-601
 * migrate-on-demand is a MANUAL operation, not an automatic heal) — run on hours-to-days, while
 * MAX_ATTEMPTS is spent in ~3 days of backoff and faster still now that ATL-983 drains on every
 * app start. Measured in prod on 2026-09-03: `attempts-exhausted` with a 401 was the single
 * largest avoidable drop class, and it destroyed `sales.create` entries — money, already
 * committed to Firestore — at attempts=9 after 62.9h, for users who existed and were active.
 *
 * This is the third time this file has learned the same lesson: ATL-902 (a dead host is not a
 * failed attempt) and ATL-911 (an offline device is not a failed attempt) reached the identical
 * conclusion. The pattern: if the only thing that can fix a failure is slower than the attempt
 * budget, the attempt budget is the wrong instrument, and MAX_AGE_MS is the right bound.
 */
export function isAuthorizationFailure(error: unknown): boolean {
  const status = replayFailureStatus(error)
  return status === 401 || status === 403
}

/**
 * ATL-1204 — the SERVER failed, and nothing about this write did.
 *
 * Any 5xx that `isMigrationPausedFailure` has not already claimed: a 500 from an unhandled fault, a
 * 502/504 from the proxy in front of Cloud Run, a 503 that says the server could not determine
 * something it needs (`write_master_undetermined`, ATL-1210's `recreate_state_unknown`, the ATL-1488
 * fence's indeterminate org). Each one is the backend saying "not now", never "not this".
 *
 * It used to sit in the generic attempt-consuming bucket, with timeouts (below), and that bucket is
 * short: ten attempts at 30 s · 2^n are spent in ~8.5 hours at the fastest (the 6 h MAX_BACKOFF_MS is
 * never reached; the largest delay applied is 4.27 h) — a till that drains often, as every app start
 * and every live sale makes it, gets close to that floor. So a backend fault that lasted a working
 * day destroyed every write a till queued during it — money already committed to Firestore — while
 * a 401, which ATL-1198 turned some of those same faults INTO, would have held them for the week.
 * The governing rule is the one `isAuthorizationFailure` states: when the only thing that can fix a
 * failure is slower than the attempt budget, MAX_AGE_MS is the right bound, not the budget.
 *
 * The cost, accepted: a 5xx that is really deterministic for one payload (a backend bug) is retried
 * at up to hourly intervals (SERVER_FAULT_RETRY_CAP_MS; a timeout at up to 6 h) for the week
 * instead of dropped after 8.5 h, holding that document's later entries behind it (rule 2) — which
 * they would have been anyway, since they cannot land without it. The upside of the same case is that a backend fix deployed
 * within the week delivers the write. A second cost, NOT confined to that document: such entries
 * now occupy the queue's shared MAX_ENTRIES / MAX_TOTAL_BYTES for the week, and overflow evicts the
 * OLDEST document group whatever held it. Oldest-first means the stuck entries themselves are
 * usually what goes, but a group held for longer still (an offline or 401 hold that predates the
 * bug) is evicted before them. Whether overflow should prefer groups with server holds is an open
 * eviction-policy decision, recorded as ATL-1887 rather than settled here.
 *
 * The client's own TIMEOUT is held the same way: the same fault seen from the other side — the server
 * did not answer in 60 s — and the largest class this queue was built for (ATL-776: 827
 * `sales.create` timeouts in one week). That the request may have executed is no reason to spend an
 * attempt: rule 1 queues only writes whose replay is idempotent, so replaying one that did execute
 * is a no-op. A timeout CAN be about the payload (ATL-776 found them biased toward the largest
 * carts); that is the accepted cost above, and dropping it after 8.5 h would lose the sale anyway.
 *
 * Which errors are a timeout is `isClientTimeoutFailure`'s question, shared with `replayFailureClass`
 * so the class an entry reports always names the branch that held it.
 */
export function isServerFaultFailure(error: unknown): boolean {
  const status = replayFailureStatus(error)
  if (status === undefined) return isClientTimeoutFailure(error)
  return status >= 500 && !isMigrationPausedFailure(error)
}

/**
 * ATL-1204 — axios gave up waiting for the server: its XHR `ontimeout` handler, and nothing else.
 *
 * That handler rejects with no response and code `ECONNABORTED` — or `ETIMEDOUT` under
 * `transitional.clarifyTimeoutError` — and the message `timeout of <n>ms exceeded` (no
 * `timeoutErrorMessage` is configured anywhere in this app). The CODE is required, so the bulk stock
 * mirror's own `Error` (no code), which can quote a per-item `statement timeout` that a retry will
 * not converge, is not a timeout. The message is required too, for both codes: axios gives
 * `ECONNABORTED` to `Request aborted` (XHR `onabort`: the client tore the request down, e.g. a page
 * unload), and a socket-level `connect ETIMEDOUT <addr>` (Node's adapter) never reached a server —
 * neither is the server failing to answer, and neither may be counted in `serverHolds` /
 * `heldServerFault`. (`/timeout/` does not match `ETIMEDOUT`, so the code inside such a message
 * cannot satisfy it.) Self-contained on purpose: it must be right when called on its own, not only
 * behind the drain's earlier transport check.
 */
export function isClientTimeoutFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: unknown; message?: unknown; response?: unknown }
  if (e.response !== undefined) return false
  if (e.code !== 'ECONNABORTED' && e.code !== 'ETIMEDOUT') return false
  return /timeout/i.test(String(e.message ?? ''))
}

/**
 * ATL-1137 — a 403 is a verdict on the caller's ROLE, and it must be VISIBLE.
 *
 * `isAuthorizationFailure` above answers the REPLAY question ("may this consume an attempt?") and
 * groups 401 with 403 because neither says anything about the payload. This predicate answers a
 * different question, on the LIVE path: "is a human being told?" — and there the two statuses are
 * opposites. A 401 is ordinary migration-state noise (an unmigrated user, a session that has not
 * restored; ATL-601 migrate-on-demand), and escalating it would refill the exact warning budget
 * this class exists to escape. A 403 is a guard evaluating a role and refusing — it does not clear
 * on its own, and nobody was being told.
 *
 * Measured in prod (QARASAY-KASSA-2AD, 15 events, `users.create`, `api.statusCode: 403`): the
 * write was reported at `warning`, tagged `dual_write_defect: False`, and fingerprinted into the
 * shared per-op transient bucket alongside 503s and timeouts, then dropped. The tag asserted the
 * opposite of the truth, and the loss sat below alert threshold — the precise invisibility the
 * ATL-782 docblock at the top of this file says a permanent loss must not sit in.
 *
 * The status alone is a sound signal because of the sender split documented on
 * `isAuthorizationFailure`: 403 reaches a dual-write only from `RolesGuard`, `PermissionsGuard`,
 * or a service-level `ForbiddenException`. Three sources, one meaning — the caller's role was
 * evaluated and refused.
 *
 * ⚠️ ATL-1152 (open, deliberately NOT addressed here). Some 403s come from rules ported into the
 * backend that evaluate over SUPABASE state while the app evaluated the same rule over FIRESTORE
 * state — e.g. "do not remove the last active admin" counted against `public.users`, which is the
 * very table the migration leaves incomplete. Those 403s refuse an action Firestore has already
 * committed, and until the two sides agree they are a real source of one-sided writes. Making
 * them alertable is ATL-1152's own recommended first step ("option 3, then 1"); ALIGNING the two
 * read sources is a separate change with a separate blast radius and is not in this one.
 *
 * ⚠️ ACCEPTED LIMITATION — matched on STATUS ALONE, unlike its neighbour `isDeadBackendFailure`,
 * which matches on body text precisely because "the two 404s mean opposite things". The same
 * asymmetry is possible for 403: a filtering proxy or captive portal on a store's wifi, or a
 * Cloud Armor / IAM refusal, would also answer 403 with no guard involved, and would be reported
 * here as a role refusal it is not. Left broad DELIBERATELY, because the two errors are not
 * symmetric. Being wrong toward "alert" costs a misleading issue whose `api.serverMessage` shows
 * an HTML or generic body rather than a role name, and loses no data (queueing is unchanged).
 * Being wrong toward "quiet" — a body test tight enough to miss a real refusal — silently
 * recreates ATL-1137 itself. ATL-902 earned its body match from 1,354 measured events; there is
 * no measured infrastructure-403 population here, and narrowing on a hypothetical is how a guard
 * ends up blocking the wrong half. Revisit if such a cluster appears; the tell is a `dual_write_
 * forbidden:True` issue whose refusal detail names no role.
 *
 * NOT permanent, and that is load-bearing: see the `isPermanentReplayFailure` docblock. A 403 is
 * cleared by an admin editing a role or by a guard fix reaching the device — hours to days — so
 * MAX_AGE_MS is its bound, exactly as it is for a dead host (ATL-902) and an offline device
 * (ATL-911). Reporting it loudly and dropping it are independent decisions; this does the first.
 */
export function isForbiddenFailure(error: unknown): boolean {
  return replayFailureStatus(error) === 403
}

/**
 * Replay due entries, per-document FIFO. Single-flight and throttled — calling this often is
 * free. On web the whole drain additionally holds a Web Lock (`ifAvailable`), so concurrent
 * tabs cannot double-replay; a tab that finds the lock taken simply skips its turn.
 *
 * The replay goes through `typedClient.replayRequest`, i.e. the same axios instance as live
 * writes: fresh bearer token per attempt, 401-retry interceptor, 60s write timeout. Imports
 * are lazy to keep this module out of the firebase.ts ↔ typedApiClient import cycle.
 */
/**
 * ATL-1459 — told of every `live-success` drain request, before the drain's own coalescing: a live
 * write just landed, so the network is up, which is when the mirror-intent journal can answer an intent
 * it had to keep (`mirrorIntent.ts` throttles itself). A listener that throws is ignored.
 */
const liveSuccessListeners = new Set<() => void>()

export function addLiveSuccessListener(listener: () => void): () => void {
  liveSuccessListeners.add(listener)
  return () => {
    liveSuccessListeners.delete(listener)
  }
}

export async function drainDualWriteOutbox(trigger: DrainTrigger): Promise<void> {
  if (trigger === 'live-success') {
    for (const listener of liveSuccessListeners) {
      try {
        listener()
      } catch {
        // A listener never stands between a live success and the outbox's own drain.
      }
    }
  }
  if (drainInFlight) return drainInFlight
  if (!UNTHROTTLED_TRIGGERS.has(trigger) && Date.now() - lastDrainAt < DRAIN_THROTTLE_MS) {
    return
  }
  const run = async () => {
    const locks = webLocks()
    if (locks) {
      await locks.request(`${WEB_LOCK_NAME}-drain`, { ifAvailable: true }, async (lock) => {
        if (lock) await drainNow(trigger)
      })
      return
    }
    await drainNow(trigger)
  }
  drainInFlight = run().finally(() => {
    drainInFlight = null
    lastDrainAt = Date.now()
  })
  return drainInFlight
}

async function drainNow(trigger: DrainTrigger): Promise<void> {
  // Snapshot WITHOUT holding the mutex during network calls — enqueue/purge from live sales
  // must never wait on a slow replay. Every entry is re-checked against the live queue right
  // before its replay (rule 4), so the snapshot only decides the candidate set and order.
  const queue = await withQueue(async (q) => ({ queue: q, result: [...q] }))
  if (queue.length === 0) {
    stopDrainInterval()
    return
  }

  const now = Date.now()
  const due = queue.filter((e) => e.nextAttemptAt <= now)
  if (due.length === 0) {
    // Entries exist but none are due — keep the tick alive so their backoff actually elapses.
    ensureDrainInterval()
    return
  }

  // The gate can have changed since enqueue (kill-switch, org removed from allowlist).
  // An informed "no" pauses the drain — entries stay queued, nothing is dropped.
  // Lazy requires (not top-level imports): this module is imported by dualWrite.ts, which is
  // imported by firebase.ts, which typedApiClient.ts imports — a top-level import here would
  // close that cycle at module-init time. `require` at call time is cycle-safe in both Metro
  // and jest (dynamic `import()` needs --experimental-vm-modules under jest).
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { describeGateAuthority } = require('./dualWrite') as {
    describeGateAuthority: () => { decision: string; informed: boolean }
  }
  const gate = describeGateAuthority()
  // ATL-983 — an UNINFORMED deny does not pause replay, and this asymmetry is deliberate.
  //
  // A replay is not a new write. The Firestore document it mirrors committed long ago, under a
  // gate that said yes; replaying only makes Supabase match what the merchant already has. What
  // the gate governs is whether to START diverging further.
  //
  // So the two denies are answered differently. An INFORMED deny — a kill switch, or an org
  // removed from the allowlist under fresh policy — is an operator's decision, and honouring it
  // is what keeps the kill switch executable. An UNINFORMED deny is the device having no policy
  // at all (the ATL-911 empty-Remote-Config signature, or a cache past its trust bound); acting
  // on it would mean the devices whose writes are failing are exactly the devices whose queue
  // never drains, which is how these two defects compounded into a month of silent loss.
  //
  // Bounded either way: entries still expire at MAX_AGE_MS, so a wrong guess here costs at most
  // one week of replay, never a permanent divergence.
  if (gate.decision !== 'allowed' && gate.informed) {
    Sentry.addBreadcrumb({
      category: 'dual_write.outbox',
      level: 'info',
      message: `outbox: drain paused by an informed gate deny (${gate.decision})`,
      data: { trigger, decision: gate.decision, queued: queue.length, due: due.length },
    })
    // eslint-disable-next-line no-console
    console.log(
      `[dualWriteOutbox] drain paused by gate (${gate.decision})`,
      JSON.stringify({ trigger, queued: queue.length, due: due.length }),
    )
    // The entries are still there and still need the tick, or an allowlist change would not be
    // noticed until the next dual-write.
    ensureDrainInterval()
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getOrganization } = require('./types') as {
    getOrganization: () => { id?: string } | null
  }
  const currentOrgId = getOrganization()?.id

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { typedClient } = require('../API/typedApiClient') as {
    typedClient: {
      replayRequest: (config: ReplayableRequestConfig & { timeout?: number }) => Promise<unknown>
    }
  }

  // ATL-1207. Lazily required for the same cycle reason as the requires above — it reaches
  // `./types` — and hoisted out of the entry loop so a queue of deletes resolves it once.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { probeDeletedEntity } = require('./deletedEntityLocation') as {
    probeDeletedEntity: (
      location: DeletedEntityLocation | undefined,
      documentId: string,
      orgFirestoreId: string,
    ) => Promise<'recreated' | 'still-deleted' | 'unknown'>
  }

  // ATL-983 — what this drain actually did. Without these, "the drain ran and everything failed"
  // and "the drain never ran" produce identical (empty) evidence, which is why the loss took a
  // month to see.
  const outcome = {
    trigger,
    queued: queue.length,
    due: due.length,
    /** Age of the oldest entry that was due this round — how far the queue is from converging. */
    oldestDueAgeMs: due.reduce((oldest, e) => Math.max(oldest, now - e.enqueuedAt), 0),
    replayed: 0,
    /**
     * Held without consuming an attempt: dead backend host, route 404, offline device, re-migration
     * pause (ATL-1805) or server fault (ATL-1204) — split below as `held*`.
     */
    held: 0,
    /** ATL-908: held without consuming an attempt because the SESSION could not be authorized. */
    heldUnauthorized: 0,
    heldForbidden: 0,
    /** Failed, attempt consumed, backing off. */
    failed: 0,
    dropped: 0,
    /** Belongs to an org this session cannot authorize (or the org is not known yet). */
    skippedOtherOrg: 0,
    /** Purged by a live write between the snapshot and the replay (rule 4). */
    skippedPurged: 0,
    /** Whole document groups waiting on an earlier entry's backoff (rule 2). */
    skippedBackoffOrdering: 0,
    /** ATL-1207: deletes dropped because Firestore says the entity is back (another device). */
    refusedRecreated: 0,
    /** ATL-1207: held without consuming an attempt because the recreate probe went unanswered. */
    heldUnverifiable: 0,
  }
  /** ATL-908: did any replay succeed this round? A success proves the session can authorize. */
  let sessionAuthorized = false
  // ATL-1445 stage 3, P8 — refused mirrors moved into outbox v2, and conversions that could not be
  // persisted (held, no attempt consumed). Kept OUT of `outcome` so a drain that converts nothing —
  // every drain until backend PR B1 — reports exactly the object it always did.
  let converted = 0
  let conversionHeld = 0
  /** P8 (review 1, S-4): entries left for after a migration that asked for the queue mid-pass. */
  let deferredToMigration = 0
  // ATL-1193 — of `outcome.replayed`, the deletes whose row was already absent. Kept OUT of
  // `outcome` for the same reason as the P8 counters: a drain that delivers no such delete reports
  // exactly the object it always did.
  let replayedAbsentDelete = 0
  // ATL-1760 — of `outcome.held`, which hold. Out of `outcome` for the same reason as the counters
  // above. `held` alone fuses a dead host, a route 404 and a device that reached nothing, and a till
  // whose queue sat at `due == held` for a month (VSH) could not say which of the three it was.
  let heldDeadBackend = 0
  let heldRouteNotFound = 0
  let heldTransport = 0
  // ATL-1805 — of `outcome.held`, the entries whose organization's dual-write is paused.
  let heldMigrationPaused = 0
  // ATL-1204 — of `outcome.held`, the entries the backend answered with a server fault.
  let heldServerFault = 0

  // Rule 2: group by document, drain each group in enqueue order, and stop a group at its
  // first transient failure so an update can never overtake its own create.
  const groups = new Map<string, OutboxEntry[]>()
  for (const entry of due) {
    const group = groups.get(entry.firestoreId)
    if (group) group.push(entry)
    else groups.set(entry.firestoreId, [entry])
  }

  // ATL-1207 — start watching live writes for the duration of the pass. Armed here rather than in
  // a `finally` because drains are serialised by `drainInFlight` and this same line disarms a set
  // left behind by a pass that threw; a stale one could only ever cause an EXTRA withdrawal, which
  // is the safe direction, and it cannot outlive the next pass.
  liveWritesDuringDrain = new Set<string>()

  for (const [documentId, group] of groups) {
    group.sort((a, b) => a.enqueuedAt - b.enqueuedAt)

    // Rule 2 holds ACROSS drain rounds, not only within one: the due-filter must not let a
    // later entry replay ahead of an EARLIER same-document entry that is merely inside its
    // backoff window. Without this gate: stores.create fails twice (backing off) → the queued
    // store-settings seed becomes due alone in the next round → replays against a store that
    // does not exist yet → 400 → permanently dropped while the create later heals. The whole
    // group waits for its earliest entry to come due again.
    //
    // "Earlier" is QUEUE POSITION, not `enqueuedAt`: the array is append-ordered (enqueue and
    // supersede both append), so position is enqueue order even when two entries land in the
    // same millisecond — which the create+seed pair does on web, where the seed's queue-only
    // throw follows the create's failure with no network round-trip in between. A timestamp
    // comparison (`enqueuedAt <`) misses that tie and reopens the exact loss above.
    const firstDueIndex = queue.findIndex((e) => e.id === group[0].id)
    const gatedByBackoff = queue.some(
      (e, index) =>
        e.firestoreId === documentId && index < firstDueIndex && e.nextAttemptAt > now,
    )
    if (gatedByBackoff) {
      outcome.skippedBackoffOrdering += group.length
      continue
    }
    for (const entry of group) {
      // P8 (review 1, S-4): a v1 → v2 migration is waiting for this drain — finish the entry in flight
      // (already returned by now) and take no more, so its wait is ONE replay, not a pass. Untouched
      // entries stay as they are and are seen by the migration or the next pass. Never true today.
      if (exclusiveRequests > 0) {
        deferredToMigration += 1
        continue
      }
      // Rule 4: skip entries purged since the snapshot (their document was deleted or
      // rewritten by a live write mid-drain).
      const stillQueued = await withQueue(async (q) => ({
        queue: q,
        result: q.some((e) => e.id === entry.id),
      }))
      if (!stillQueued) {
        outcome.skippedPurged += 1
        continue
      }

      // Expiry is checked BEFORE the org check, and that order is load-bearing (ATL-983). An
      // entry past MAX_AGE_MS is unreplayable by ANY session, so gating its expiry on "is this
      // the org that owns it" left foreign entries in the queue permanently — immortal rows that
      // also kept the idle tick running forever. The comment below used to claim they "still
      // expire by age"; with the org `break` above them, they never did.
      if (Date.now() - entry.enqueuedAt > MAX_AGE_MS) {
        await removeEntry(entry.id)
        reportDrop(entry, 'expired')
        outcome.dropped += 1
        continue
      }

      // Entries for another org — or an unknown current org — can't be authorized by this
      // session; leave them for the session that owns them (they now really do expire by age,
      // per the check above).
      if (!currentOrgId || entry.orgFirestoreId !== currentOrgId) {
        // This entry and every later one in its group — the break leaves them all untouched.
        outcome.skippedOtherOrg += group.length - group.indexOf(entry)
        break
      }

      // ATL-1207 — a delete may only fire while the entity it removed is STILL GONE.
      //
      // Every other supersede rule in this module is fed by writes made in THIS process: the
      // cross-label purge in `noteDualWriteSuccess` needs a live write on this device, and the
      // pending-skip queue's guard 1 needs one in this session. A recreate on ANOTHER till is
      // invisible to both, and replaying over it removes a row the customer can see in Firestore
      // — strictly worse than the loss this queue exists to prevent, so it is checked against the
      // one store every device converges on rather than assumed away.
      //
      // Only entries whose caller STATED a location are checked; see `deletedEntityLocation` for
      // why that is the reusable-id population and not every delete. Placed here, after the
      // re-check/expiry/org gates and immediately before the request, so the window between the
      // read and the delete is the same single in-flight request outbox rule 4 already accepts.
      if (entry.deletedFrom) {
        // eslint-disable-next-line no-await-in-loop
        const probed = await probeDeletedEntity(
          entry.deletedFrom,
          entry.firestoreId,
          entry.orgFirestoreId,
        )
        // ATL-1209 — a FIELD-STATE precondition has no safe direction to fail toward. The
        // live-write check below withdraws a delete because withdrawing KEEPS a row; for a user's
        // `active` flag withdrawing loses the disable/enable just as surely as replaying a stale
        // one would, and the live write that tripped it may not have touched the field at all (a
        // `users.update` rename keys on the same uid). So a field-state answer that a live write
        // for this document may have outdated is not acted on in either direction: it is held,
        // and the next pass asks Firestore again, after that write.
        const verdict =
          probed === 'still-deleted' &&
          entry.deletedFrom.field !== undefined &&
          liveWritesDuringDrain?.has(entry.firestoreId)
            ? 'unknown'
            : probed
        if (verdict === 'recreated') {
          await removeEntry(entry.id)
          reportRecreated(entry)
          outcome.refusedRecreated += 1
          // The document's LATER entries are not stale by this: they were queued after the delete
          // and describe the row that exists now. Only this one is withdrawn.
          continue
        }
        if (verdict === 'unknown') {
          // Nothing was established, so nothing may be destroyed. Held on its own counter — an
          // unanswered question is not a failed attempt (ATL-902/911/908 reached the same
          // conclusion for a dead host, an offline device and an unauthorized session) — and
          // bounded by MAX_AGE_MS, which the expiry check above applies before this one.
          const probeHolds = (entry.probeHolds ?? 0) + 1
          // `lastError` is deliberately left alone: it holds WHY this write was queued, which is
          // what `reportDrop` reports if the entry later expires. Overwriting it with "the probe
          // did not answer" would trade the cause for the symptom — the counter above already
          // records that this happened, and how often.
          await updateEntry(entry.id, {
            probeHolds,
            nextAttemptAt: Date.now() + Math.min(BASE_BACKOFF_MS * 2 ** probeHolds, MAX_BACKOFF_MS),
          })
          // The whole rest of the group, not just this entry — same accounting as the other-org
          // `break` above. The drain outcome is the only evidence this module leaves behind, and a
          // counter that omits the entries the `break` skipped makes `due` stop reconciling with
          // the outcomes, which is precisely the "did the drain look at this?" ambiguity ATL-983
          // added these numbers to end.
          outcome.heldUnverifiable += group.length - group.indexOf(entry)
          // Rule 2: this document's later entries wait behind the held one.
          break
        }

        // A `still-deleted` verdict is only as fresh as the read that produced it, and that read
        // is a SERVER ROUND-TRIP taken before this line. A live write for this same document
        // landing inside that window — the cashier re-adding the payment method on THIS device
        // while the drain is mid-probe — is invisible to the answer: Firestore served the state
        // it held when the read was served, so the probe says "gone" about a document that is
        // back, and the replay removes the row the live write just created. That is this ticket's
        // own loss, reached through its own guard.
        //
        // Rule 3's purge cannot cover it: a live CREATE purges only its SAME-LABEL entries
        // (`noteDualWriteSuccess`), which is exactly why the probe exists. So the pass records
        // every live write it saw and the verdict is checked against it — no extra I/O, and it
        // fails toward keeping the row, the direction this module chooses everywhere else.
        if (liveWritesDuringDrain?.has(entry.firestoreId)) {
          await removeEntry(entry.id)
          reportRecreated(entry)
          outcome.refusedRecreated += 1
          continue
        }
      }

      try {
        const response = await typedClient.replayRequest(entry.request)

        // ATL-962 — a 200 is not automatically a success.
        //
        // `/products/stock/bulk` answers 200 with a per-item `results[].ok` (its documented
        // failure-isolation contract), so "the replay did not throw" accepted a replay in which
        // EVERY item failed: the entry was removed, no console line, no Sentry event, no drop
        // record — the divergence stayed in the books and the evidence of it did not. Measured on
        // the `stock-mirror-item-failed` cluster: 34 of 35 classified events were `Product not
        // found` and one a `numeric field overflow`, none of which a retry can converge, so every
        // one of those live failures was followed by exactly one silent, unrecorded drop.
        //
        // Throwing here rather than special-casing the drop keeps ONE bounding mechanism for the
        // whole outbox: the failure consumes an attempt, backs off, and ends at
        // `attempts-exhausted` → `reportDrop`, exactly as a permanent refusal does.
        if (isBulkStockMirrorRequest(entry.request)) {
          const failedItems = failedBulkStockItems(response)
          if (failedItems.length > 0) {
            reportFailedStockMirrorItems(entry.firestoreId, failedItems, 'replay')
            throw new Error(
              `replay of ${entry.label} returned 200 with ${failedItems.length} failed item(s): ` +
                failedItems
                  .map((i) => `${i.storeId}: ${String(i.error ?? 'unknown').slice(0, 120)}`)
                  .join('; '),
            )
          }
        }

        await removeEntry(entry.id)
        outcome.replayed += 1
        sessionAuthorized = true
        Sentry.addBreadcrumb({
          category: 'dual_write.outbox',
          level: 'info',
          message: `outbox: replayed ${entry.label} OK`,
          data: { label: entry.label, firestoreId: entry.firestoreId, attempts: entry.attempts + 1, trigger },
        })
        // eslint-disable-next-line no-console
        console.log(
          `[dualWriteOutbox] replayed ${entry.label} OK`,
          JSON.stringify({ entryId: entry.id, firestoreId: entry.firestoreId, attempts: entry.attempts + 1 }),
        )
      } catch (error) {
        // ATL-1445 stage 3, P8 — dormant while no organization is Supabase-master (B1 emits it only
        // for one). A 409 `WRITE_MASTER_SUPABASE` naming this
        // entry's organization says Firestore is no longer the master: this mirror must not be lost as
        // a "permanent refusal", it IS the write: it moves into outbox v2 as a master write, or is held
        // here and paged — never dropped (`global/Write/mirrorRefusalConversion.ts`). Detected
        // synchronously and without I/O; for
        // every other error — every response today's backend can send — `masterRefusalOf` is null
        // and nothing below this block changes.
        if (masterRefusalOf(error) !== null) {
          // eslint-disable-next-line no-await-in-loop
          const conversion = await convertRefusedMirrorEntry(entry, error)
          if (conversion === 'converted') {
            // Only now: v2 has reported the copy durable (or already held it, after a crash).
            await removeEntry(entry.id)
            converted += 1
            continue
          }
          // Review 1, S-2: B1's refusal is NEVER dropped. Whatever kept it from converting — v2 or the
          // quarantine did not take it, or the app classifies its route differently from the backend —
          // the entry stays where it is (no attempt consumed), is paged by the conversion, and is
          // re-sent (and so re-decided) after the backoff. v1's 7-day bound still applies.
          const conversionHolds = (entry.conversionHolds ?? 0) + 1
          await updateEntry(entry.id, {
            conversionHolds,
            nextAttemptAt: Date.now() + Math.min(BASE_BACKOFF_MS * 2 ** conversionHolds, MAX_BACKOFF_MS),
          })
          conversionHeld += 1
          // Rule 2: this document's later entries wait behind the held one.
          break
        }
        // ATL-1193 — the replay shares the live path's 404 contract for deletes. A delete that
        // reached the backend and got the entity-absent envelope 404 has nothing to do: the row is
        // not in Supabase (never mirrored because it predates the org's dual-write, or already
        // deleted), so Supabase already says what Firestore says. `bestEffortDualWriteDelete` resolves that as a success on the live path,
        // and outbox v2 classifies it `delivered` (`outboxClassify.ts`, `entity-absent-delete`).
        // This drain used to fall through to the attempt counter, spend all ten attempts, and drop
        // the entry as `attempts-exhausted`: a reported permanent loss of nothing, with rule 2
        // parking the document's later entries behind it meanwhile.
        //
        // So it takes the SUCCESS path, all of it: removed, counted as replayed, and the loop
        // continues down the document's FIFO, exactly as a 2xx would. A live delete supersedes the
        // whole document when it is enqueued, so what follows it is USUALLY newer. Not always: a
        // pending-skip replay that fails is enqueued `fromReplay` with enqueuedAt = now, so an OLDER
        // create can sort behind this delete and resurrect the row after it. That is pre-existing
        // and identical for a 2xx (ATL-1761); it is not a reason to hold here. It is ordered after the master-refusal conversion (a 409, never a 404) and
        // ahead of every hold, and `isEntityAbsentFailure` already refuses a dead-host, route or
        // envelope-less 404, so those keep their holds and attempts below. Non-delete labels keep
        // 404 retryable: a create whose parent is missing can still converge.
        //
        // `sessionAuthorized`: the envelope comes from the backend's exception filter after
        // `FirebaseAuthGuard` let the request through, the same proof a 2xx gives.
        if (isDeleteLabel(entry.label) && isEntityAbsentFailure(error)) {
          await removeEntry(entry.id)
          outcome.replayed += 1
          replayedAbsentDelete += 1
          sessionAuthorized = true
          Sentry.addBreadcrumb({
            category: 'dual_write.outbox',
            level: 'info',
            message: `outbox: replayed ${entry.label}, row already absent`,
            data: { label: entry.label, firestoreId: entry.firestoreId, attempts: entry.attempts + 1, trigger },
          })
          // eslint-disable-next-line no-console
          console.log(
            `[dualWriteOutbox] replayed ${entry.label}: row already absent`,
            JSON.stringify({ entryId: entry.id, firestoreId: entry.firestoreId, attempts: entry.attempts + 1 }),
          )
          continue
        }
        if (isPermanentReplayFailure(error)) {
          await removeEntry(entry.id)
          reportDrop(entry, 'permanent-refusal', error)
          // ATL-1487: a queued write REFUSED on replay is the same permanent loss as one refused
          // live, so it is counted in the same PostHog population (`path: 'replay'`).
          if (isRefusedWriteFailure(error)) reportRefusedWrite(entry.label, error, 'replay')
          outcome.dropped += 1
          continue
        }
        // ATL-902: a dead HOST is not a failed attempt, it is the same impossibility every time.
        // Counting it burned all 10 attempts inside ~10 hours and dropped the write — while the
        // only thing that can ever fix it (this device taking an OTA onto a bundle built against
        // the live host) takes days. So hold the entry at a fixed interval WITHOUT consuming an
        // attempt and let MAX_AGE_MS bound it instead: if the device updates inside the week, the
        // queued writes replay against the real backend and the org heals itself. Deliberately
        // NOT treated as permanent — dropping it immediately would forfeit that recovery.
        //
        // ATL-1194 puts the ROUTE 404 in the same bucket, and it belongs there by that same
        // sentence. The predicate that creates the class is on the LIVE path; without this line
        // the class it creates lands in the generic branch below and spends all ten attempts in
        // ~8.5 hours (a 30s enqueue delay plus nine doubling backoffs, 60s…15,360s — MAX_BACKOFF_MS
        // is 6h and is never actually reached; the largest applied is 4.27h). Two of the causes on
        // `isRouteNotFoundFailure` cannot heal in 8.5 hours: a device on a stale BASE URL is fixed
        // only by taking an OTA (measured at ~4 days to 93% adoption), and a proxy misrouting a
        // path is fixed by an operator. So a route 404 would have been queued — the whole point of
        // ATL-1194 — and then dropped as `attempts-exhausted` before the only thing that could fix
        // it arrived. That is ATL-902's own failure reproduced on the class introduced to prevent
        // it.
        //
        // ⚠️ ONE CAUSE THIS HOLD DOES NOT RESCUE, and an earlier draft of this comment claimed it
        // did: a route RENAME. `replayRequest` re-sends the entry's STORED `url` — only the base
        // URL comes from the running bundle — so an OTA fixes future live writes and never the
        // queued ones, which keep asking for the old path. For that cause the hold buys 28 futile
        // requests over 7 days instead of 10 over 8.5 hours and loses either way; MAX_AGE_MS is
        // what bounds it. It is kept because the other two causes DO recover, and because a
        // 7-day-old delete that finally lands still removes the row while a dropped one never can.
        //
        // The third cause (an endpoint not deployed yet) heals faster than this hold retries, and
        // pays a slower first retry for it. That is the same trade the dead-host branch already
        // makes, and it is the right way round: a delete that replays late still removes the row,
        // a delete that was dropped never does.
        if (
          isDeadBackendFailure(error) ||
          isTransportFailure(error) ||
          isRouteNotFoundFailure(error)
        ) {
          // ATL-911 extends the same no-attempt hold to TRANSPORT failures, for the same reason.
          //
          // A request that never reached a server is not an attempt the server refused — it is
          // the device being offline. Counting it burned the 10-attempt budget in ~10 hours
          // against a 7-day MAX_AGE_MS, so a till that lost connectivity over a weekend dropped
          // its queued writes as `attempts-exhausted` and reported them as permanent losses,
          // having never once put the payload in front of the backend.
          //
          // Held at the same fixed interval and bounded by MAX_AGE_MS instead: a device that
          // regains connectivity inside the week replays everything it queued.
          const heldClass = replayFailureClass(error)
          await updateEntry(entry.id, {
            nextAttemptAt: Date.now() + DEAD_BACKEND_RETRY_MS,
            lastError: errorMessage(error),
            lastErrorClass: heldClass,
          })
          outcome.held += 1
          if (heldClass === 'dead-backend') heldDeadBackend += 1
          else if (heldClass === 'route-404') heldRouteNotFound += 1
          else heldTransport += 1
          // Rule 2 still applies: this document's later entries wait behind the held one.
          break
        }
        // ATL-1805: the organization is being re-migrated and its dual-write is paused. Nothing
        // executed, and the same request is accepted once the pause lifts — so it holds WITHOUT
        // consuming an attempt, on its own doubling counter capped at 15 minutes, bounded by
        // MAX_AGE_MS. The write is NOT delivered until a non-pause answer says so: a run that loaded
        // it makes the replay an idempotent no-op, and one that did not (its collection already read,
        // or the run failed first) is exactly the write this hold exists to deliver.
        if (isMigrationPausedFailure(error)) {
          const pauseHolds = (entry.pauseHolds ?? 0) + 1
          await updateEntry(entry.id, {
            pauseHolds,
            nextAttemptAt:
              Date.now() + Math.min(MIGRATION_PAUSE_RETRY_BASE_MS * 2 ** pauseHolds, MIGRATION_PAUSE_RETRY_CAP_MS),
            // `serverBodyMessage` unwraps the filter envelope the replay path receives raw
            // (`serverRefusalDetail` does not — ATL-1189), so the entry says it was a pause.
            lastError: serverBodyMessage(error) || errorMessage(error),
            lastErrorClass: 'migration-paused',
          })
          outcome.held += 1
          heldMigrationPaused += 1
          // The pause answers only after `FirebaseAuthGuard` admitted the request (guards precede
          // interceptors; the books gate runs inside the handler), the same proof a 2xx gives — so a
          // 401 hold behind it is retired exactly as after a delivered replay.
          sessionAuthorized = true
          // Rule 2: this document's later entries wait behind the held one.
          break
        }
        // ATL-908: an auth refusal holds WITHOUT consuming an attempt (see
        // `isAuthorizationFailure`), backing off on its own counter so a session that cannot be
        // authorized does not become a retry loop. Bounded by MAX_AGE_MS like every other hold.
        // Ordered AFTER the permanent check so a 400 still drops, and after the transport check
        // so an offline device keeps its own (cheaper) hold.
        if (isAuthorizationFailure(error)) {
          const authHolds = (entry.authHolds ?? 0) + 1
          // ATL-1137: remember WHICH refusal it was. A 401 and a 403 hold identically here and
          // differ in exactly one place — the retirement below — and at drop time, where a 403
          // that expired must not be reported with the shape of a device that was offline.
          //
          // Written UNCONDITIONALLY (review round 2). Setting it only when true made it sticky —
          // "this entry saw a 403 once" rather than "the last refusal was a 403" — and sticky is
          // wrong in both directions the flag is read. An entry refused once on role and then
          // repeatedly on session would permanently lose the ATL-908 retirement (the parked-at-6h
          // state ATL-908 exists to remove), and its expiry would be filed under `forbidden` while
          // every refusal after the first was a 401 — the same fusion, now pointing the other way.
          // Unconditional self-corrects both ways on the very next replay.
          const forbidden = isForbiddenFailure(error)
          await updateEntry(entry.id, {
            authHolds,
            forbidden,
            nextAttemptAt: Date.now() + Math.min(AUTH_RETRY_BASE_MS * 2 ** authHolds, MAX_BACKOFF_MS),
            // The server's own words, not just "status code 401". Without this, "this session
            // sent no token" and "this user was never migrated" — different bugs with different
            // owners — are the same event; every 401 drop in prod carried no refusal detail.
            lastError: serverRefusalDetail(error) || errorMessage(error),
            lastErrorClass: replayFailureClass(error),
          })
          outcome.heldUnauthorized += 1
          if (forbidden) outcome.heldForbidden += 1
          // Rule 2: this document's later entries wait behind the held one.
          break
        }
        // ATL-1204: a server fault holds WITHOUT consuming an attempt (see `isServerFaultFailure`),
        // on its own doubling counter capped at 1 h (6 h for a timeout: `serverFaultRetryCapMs`),
        // bounded by MAX_AGE_MS. After the auth branch, so nothing above changes; the pause 503 was
        // claimed earlier still.
        if (isServerFaultFailure(error)) {
          const serverHolds = (entry.serverHolds ?? 0) + 1
          await updateEntry(entry.id, {
            serverHolds,
            nextAttemptAt: Date.now() + Math.min(BASE_BACKOFF_MS * 2 ** serverHolds, serverFaultRetryCapMs(error)),
            // The server's own words, as the pause branch keeps them. This hold ends as `expired`,
            // and `reportDrop` has no error then — only this field — so the bare "status code 503"
            // would leave `supply_payment_not_yet_applied`, `write_master_undetermined` and
            // `books_write_state_unknown` indistinguishable in the drop — and the body alone would
            // lose the status and the `code` (`serverFaultDetail`). Capped: a proxy's 502/504 can be
            // a whole HTML page, and this is persisted with the queue.
            lastError: serverFaultDetail(error),
            // 'server-error' or 'timeout' — the two are held alike but answer different questions.
            lastErrorClass: replayFailureClass(error),
          })
          outcome.held += 1
          heldServerFault += 1
          // Rule 2: this document's later entries wait behind the held one.
          break
        }
        const attempts = entry.attempts + 1
        if (attempts >= MAX_ATTEMPTS) {
          await removeEntry(entry.id)
          reportDrop(entry, 'attempts-exhausted', error)
          outcome.dropped += 1
          // The document's later entries stay paused this round (their turn comes next drain).
          break
        }
        const backoff = Math.min(BASE_BACKOFF_MS * 2 ** attempts, MAX_BACKOFF_MS)
        await updateEntry(entry.id, {
          attempts,
          nextAttemptAt: Date.now() + backoff,
          lastError: errorMessage(error),
          lastErrorClass: replayFailureClass(error),
        })
        outcome.failed += 1
        // Rule 2: do not attempt this document's remaining entries behind a failed one.
        break
      }
    }
  }

  // ATL-1207 — the pass is over; nothing after this line reads a probe verdict.
  liveWritesDuringDrain = null

  // ATL-908 round 2 — `authHolds` must not be monotone.
  //
  // The backoff doubles to the same 6h cap after ~10 holds, and the refusals need not share one
  // cause: `getAuthToken` throwing sends the request with NO Authorization header (a guaranteed
  // 401 that is really a connectivity blip), and the `org-ready` drain can fire in the window
  // where the org atom has hydrated but `auth.currentUser` has not restored. Ten such scattered
  // blips would park a perfectly healable write at 6h intervals long after auth was fine —
  // exactly what the AUTH_RETRY_BASE_MS docblock rejects a 6h floor to avoid.
  //
  // A replay that SUCCEEDS is proof this session can authorize, which is the one observation that
  // retires every auth hold behind it. Reset those entries and make them due immediately; the
  // per-document ordering rules still decide what actually replays next round.
  //
  // ATL-1137 — but ONLY a 401's hold. That premise ("a success proves this session can authorize")
  // is true of authentication and false of authorization: a 403 is a verdict on the caller's role
  // FOR THAT ENDPOINT, so `sales.create` landing says nothing about whether `users.create` is
  // permitted. Retiring a 403 on someone else's success did two things, both bad. It defeated the
  // backoff this branch exists to impose — a permanently-refused entry sharing a queue with
  // healthy ones was re-fired on EVERY drain for the full 7 days, thousands of requests where the
  // AUTH_RETRY_BASE_MS docblock budgets ~40. And it erased the evidence: `reportDrop` reads
  // `authHolds` off the entry, so a role-refused write that finally expired was reported with
  // `authHolds: 0` and `dual_write_outbox_auth_held: False` — indistinguishable from a device that
  // was offline all week, which is the exact fusion the comment in `reportDrop` says must not
  // happen. A 403 now keeps its hold, its doubling backoff, and its label all the way to the drop.
  //
  // The guard is on the SNAPSHOT and is exact, not an optimisation that risks skipping a needed
  // retirement: an entry carrying a hold either carried it when this round started (it is in
  // `queue`, which is the whole queue and not just the due set) or acquired it in this round
  // (`heldUnauthorized`). An entry enqueued mid-drain has no hold by construction. Without it,
  // every drain that replayed anything rewrote the entire persisted queue — on web a
  // `JSON.stringify` + `localStorage` write of up to MAX_TOTAL_BYTES, on the `live-success`
  // trigger, i.e. after a sale.
  // ATL-1137 keeps the guard in step with the map below, so a round whose only holds were forbidden
  // does not rewrite the whole persisted queue to change nothing (the JSON.stringify + localStorage
  // write this guard exists to avoid, on the `live-success` trigger, i.e. after every sale).
  //
  // Be precise about what is GUARANTEED, because the two directions are not equally serious. The
  // guard can never SKIP a needed retirement — the loss-bearing direction — and that is exact by
  // enumeration: an entry satisfying the map either carried a non-forbidden hold in the snapshot
  // (`queue.some`, which is the whole queue), or acquired/flipped one this round, which necessarily
  // incremented `heldUnauthorized` without `heldForbidden` and so made `retirableHeldThisRound ≥ 1`;
  // an entry enqueued mid-drain has no holds by construction. The other direction — a guard wider
  // than the map — costs one redundant whole-queue write and changes no entry, which is why this is
  // written as an efficiency guard rather than a correctness one. It is exact only because
  // `forbidden` tracks the LAST refusal: while it was sticky, an entry now holding on 401 counted
  // as retirable in the guard and was skipped by the map, which is that redundant write.
  const retirableHeldThisRound = outcome.heldUnauthorized - outcome.heldForbidden
  if (
    sessionAuthorized &&
    (retirableHeldThisRound > 0 || queue.some((e) => e.authHolds && !e.forbidden))
  ) {
    await withQueue(async (q) => ({
      queue: q.map((e) =>
        e.authHolds && !e.forbidden ? { ...e, authHolds: 0, nextAttemptAt: Date.now() } : e,
      ),
      result: undefined,
    }))
  }

  const remaining = await withQueue(async (q) => ({ queue: q, result: q.length }))
  // ATL-1760: every drop of this pass happened in the loop above (the early returns precede it).
  flushDropTally('drain')
  reportDrainOutcome({
    ...outcome,
    remaining,
    // P8: present only when a conversion happened, so today's outcome object is unchanged.
    ...(converted > 0 ? { converted } : {}),
    ...(conversionHeld > 0 ? { conversionHeld } : {}),
    ...(deferredToMigration > 0 ? { deferredToMigration } : {}),
    ...(replayedAbsentDelete > 0 ? { replayedAbsentDelete } : {}),
    ...(heldDeadBackend > 0 ? { heldDeadBackend } : {}),
    ...(heldRouteNotFound > 0 ? { heldRouteNotFound } : {}),
    ...(heldTransport > 0 ? { heldTransport } : {}),
    ...(heldMigrationPaused > 0 ? { heldMigrationPaused } : {}),
    ...(heldServerFault > 0 ? { heldServerFault } : {}),
  })
  // P8: the backend says this organization is Supabase-master. Deliver what was converted and have the
  // watcher re-read the flag (stage-3 §4/§14); never awaited, never able to fail the drain.
  if (converted > 0 && currentOrgId) notifyMirrorsConverted(currentOrgId)

  // The tick is a property of the QUEUE, not of a recent enqueue (ATL-983): arm it whenever
  // anything is left, stop it the moment nothing is.
  if (remaining === 0) stopDrainInterval()
  else ensureDrainInterval()
}

/**
 * Say what the drain did — every time, cheaply, and loudly when it achieved nothing.
 *
 * A breadcrumb always: it costs nothing and it attaches the drain's behaviour to whatever event
 * the session reports later. A Sentry event only when the drain had work and healed none of it,
 * because that is the state that was previously indistinguishable from the drain never having
 * run — and it is the state in which writes are being permanently lost.
 *
 * "No current org" is excluded deliberately: it is the expected pre-login pass, and it resolves
 * itself on the `org-ready` trigger seconds later.
 */
function reportDrainOutcome(outcome: {
  trigger: DrainTrigger
  queued: number
  due: number
  oldestDueAgeMs: number
  replayed: number
  held: number
  heldUnauthorized: number
  heldForbidden: number
  failed: number
  dropped: number
  skippedOtherOrg: number
  skippedPurged: number
  skippedBackoffOrdering: number
  refusedRecreated: number
  heldUnverifiable: number
  remaining: number
  /** ATL-1445 P8: refused mirrors moved into outbox v2 (absent when none). */
  converted?: number
  /** ATL-1445 P8: conversions that could not be persisted, held without an attempt (absent when none). */
  conversionHeld?: number
  /** ATL-1445 P8: entries left for a migration that asked for the queue mid-pass (absent when none). */
  deferredToMigration?: number
  /** ATL-1193: of `replayed`, deletes whose row was already absent (absent when none). */
  replayedAbsentDelete?: number
  /** ATL-1760: of `held`, by which no-attempt hold (each absent when none). */
  heldDeadBackend?: number
  heldRouteNotFound?: number
  heldTransport?: number
  /** ATL-1805: of `held`, entries refused `503 migration_paused` (absent when none). */
  heldMigrationPaused?: number
  /** ATL-1204: of `held`, entries answered with a server fault (absent when none). */
  heldServerFault?: number
}): void {
  Sentry.addBreadcrumb({
    category: 'dual_write.outbox',
    level: outcome.replayed === 0 && outcome.due > 0 ? 'warning' : 'info',
    message: `outbox: drain (${outcome.trigger}) replayed ${outcome.replayed}/${outcome.due} due, ${outcome.remaining} left`,
    data: outcome,
  })

  // ATL-1207: a refused recreate is CONVERGENCE, not a stalled drain — the entry left the queue
  // and Supabase already holds the row the delete would have removed. Counting it as "healed
  // nothing" would report a working guard as a loss, once an hour, for as long as the condition
  // lasts.
  // ATL-1445 P8: a conversion is progress too — the entry left this queue for outbox v2.
  const healedNothing =
    outcome.due > 0 &&
    outcome.replayed === 0 &&
    outcome.dropped === 0 &&
    outcome.refusedRecreated === 0 &&
    !outcome.converted &&
    // A pass a migration cut short did not try (P8, S-4).
    !outcome.deferredToMigration
  // Not converging, as opposed to merely backing off — see DRAIN_STALL_MIN_AGE_MS.
  const notConverging = outcome.oldestDueAgeMs >= DRAIN_STALL_MIN_AGE_MS
  // Pre-login is the expected state of the `app-start` pass and resolves on `org-ready`.
  const blockedOnlyByOrg = outcome.skippedOtherOrg === outcome.due
  if (!healedNothing || !notConverging || blockedOnlyByOrg) return

  const now = Date.now()
  if (now - lastStallReportAt < DRAIN_STALL_REPORT_INTERVAL_MS) return
  lastStallReportAt = now

  // eslint-disable-next-line no-console
  console.warn('[dualWriteOutbox] drain healed nothing', JSON.stringify(outcome))
  // ATL-1354 — a stalled drain is a CONDITION with counts, not something that threw. It was
  // `Sentry.captureMessage`, and on a 5,000/month error allowance a condition that repeats once an
  // hour for as long as it lasts is exactly what exhausts the budget a real crash needs. The
  // breadcrumb above already carries the same numbers into whatever throws next, for free.
  //
  // Every field here is a count, a duration or an enum — the queue's SHAPE. No entry labels, no
  // firestore ids, nothing naming a document or a person.
  captureOperationalEvent('dual_write_outbox_stalled', {
    dual_write_outbox_stalled: true,
    dual_write_outbox_trigger: outcome.trigger,
    queued: outcome.queued,
    due: outcome.due,
    oldestDueAgeMs: outcome.oldestDueAgeMs,
    replayed: outcome.replayed,
    held: outcome.held,
    heldUnauthorized: outcome.heldUnauthorized,
    heldForbidden: outcome.heldForbidden,
    heldUnverifiable: outcome.heldUnverifiable,
    failed: outcome.failed,
    dropped: outcome.dropped,
    refusedRecreated: outcome.refusedRecreated,
    skippedOtherOrg: outcome.skippedOtherOrg,
    skippedPurged: outcome.skippedPurged,
    // The field that separates "backing off" from "wedged". It survived on the breadcrumb, but a
    // breadcrumb is not queryable, and this is the number you would want to group by.
    skippedBackoffOrdering: outcome.skippedBackoffOrdering,
    remaining: outcome.remaining,
    ...(outcome.conversionHeld ? { conversionHeld: outcome.conversionHeld } : {}),
    ...(outcome.replayedAbsentDelete ? { replayedAbsentDelete: outcome.replayedAbsentDelete } : {}),
    ...(outcome.heldDeadBackend ? { heldDeadBackend: outcome.heldDeadBackend } : {}),
    ...(outcome.heldRouteNotFound ? { heldRouteNotFound: outcome.heldRouteNotFound } : {}),
    ...(outcome.heldTransport ? { heldTransport: outcome.heldTransport } : {}),
    ...(outcome.heldMigrationPaused ? { heldMigrationPaused: outcome.heldMigrationPaused } : {}),
    ...(outcome.heldServerFault ? { heldServerFault: outcome.heldServerFault } : {}),
  })
}

// ============================================================================
// ATL-1445 stage 3, P8 — the 409 conversion on REPLAY (dormant until backend PR B1)
// ============================================================================

/** What the drain needs from `global/Write/mirrorRefusalConversion.ts`, required lazily. */
interface MirrorRefusalConversion {
  masterRefusalOf: (error: unknown) => { organizationFirestoreId: string } | null
  convertRefusedV1Entry: (entry: OutboxEntry, error: unknown) => Promise<'converted' | 'held'>
  notifyMirrorsConverted: (orgFirestoreId: string) => void
}

/**
 * Lazily, for the same cycle reason as the drain's other requires (the conversion reaches outbox v2,
 * whose classifier imports this module), and so that nothing of outbox v2 is loaded until a 409 is
 * actually seen. A module that fails to load is `null`: today's handling applies.
 */
function mirrorRefusalConversion(): MirrorRefusalConversion | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('../Write/mirrorRefusalConversion') as MirrorRefusalConversion
  } catch {
    return null
  }
}

/**
 * B1's refusal (`WRITE_MASTER_SUPABASE` + the organization) carried by `error`, or null. Synchronous and
 * free of I/O: anything that is not a 409 never even loads the conversion module, and a 409 without
 * the contract's body — every 409 today's backend sends — is null.
 */
function masterRefusalOf(error: unknown): { organizationFirestoreId: string } | null {
  if (replayFailureStatus(error) !== 409) return null
  return mirrorRefusalConversion()?.masterRefusalOf(error) ?? null
}

/** Convert a refused entry into outbox v2; a conversion that throws is `held` (kept, never dropped). */
async function convertRefusedMirrorEntry(
  entry: OutboxEntry,
  error: unknown,
): Promise<'converted' | 'held'> {
  const conversion = mirrorRefusalConversion()
  if (!conversion) return 'held'
  try {
    return await conversion.convertRefusedV1Entry(entry, error)
  } catch {
    return 'held'
  }
}

function notifyMirrorsConverted(orgFirestoreId: string): void {
  try {
    mirrorRefusalConversion()?.notifyMirrorsConverted(orgFirestoreId)
  } catch {
    // Delivery and the re-read are follow-ups; the entries are already durable in outbox v2.
  }
}

/**
 * A queued delete withdrawn because the entity is back (ATL-1207).
 *
 * NOT a `reportDrop`, and the difference is the point: a drop is a permanent Supabase LOSS, which
 * is why that reporter says so in as many words and files at `error`. This is the opposite —
 * Supabase holds the row, Firestore holds it, the two agree, and firing the delete is what would
 * have broken that. It is still an EVENT rather than a breadcrumb because the population has to be
 * countable: how often two devices race a delete against a recreate is the measurement that says
 * whether the seven-day replay window is the right one, and a breadcrumb can only ever be read as
 * context on some other event.
 */
function reportRecreated(entry: OutboxEntry): void {
  const detail = {
    label: entry.label,
    firestoreId: entry.firestoreId,
    orgFirestoreId: entry.orgFirestoreId,
    deletedFrom: entry.deletedFrom,
    attempts: entry.attempts,
    ...(entry.probeHolds ? { probeHolds: entry.probeHolds } : {}),
    ageMs: Date.now() - entry.enqueuedAt,
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[dualWriteOutbox] WITHDREW ${entry.label} — the entity was recreated, replaying would destroy it`,
    JSON.stringify(detail),
  )
  // ATL-1354 — this reporter's own docblock argued for a Sentry EVENT because "the population has
  // to be countable" and "a breadcrumb can only ever be read as context on some other event". Both
  // are right, and both are the argument for PostHog rather than for the error allowance: it is one
  // event PER ENTRY, unthrottled, tagged with an organization id. Left on Sentry it would keep
  // doing exactly what exhausted a 5,000/month plan nine days into a cutover ramp — and the
  // countability it asks for is precisely what PostHog gives and Sentry's grouping does not.
  Sentry.addBreadcrumb({
    category: 'dual_write.outbox',
    level: 'warning',
    message: `dual-write outbox withdrew ${entry.label} (entity recreated)`,
    data: detail,
  })
  captureOperationalEvent('dual_write_outbox_recreated', {
    dual_write_outbox_recreated: true,
    dual_write: entry.label,
    ...splitLabelTags(entry.label),
    // No `dual_write_org` and no `firestoreId`: PostHog resolves the organization from the person,
    // and the document id identifies a row rather than counting a population. Both stay on the
    // breadcrumb and in the console line above, where an incident responder still has them.
    deletedFrom: entry.deletedFrom ? String(entry.deletedFrom) : null,
    attempts: entry.attempts,
    ageMs: detail.ageMs,
  })
}

async function removeEntry(id: string): Promise<void> {
  await withQueue(async (queue) => ({ queue: queue.filter((e) => e.id !== id), result: undefined }))
}

async function updateEntry(id: string, patch: Partial<OutboxEntry>): Promise<void> {
  await withQueue(async (queue) => ({
    queue: queue.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    result: undefined,
  }))
}

// ============================================================================
// Loud drops — a dropped entry is a permanent Supabase loss; silence here would recreate
// the exact invisibility this module exists to end (ATL-664 gate-honesty).
// ============================================================================

/**
 * `module.op` → the two tags the failure path already sets, so a drop and its originating failure
 * can be filtered the same way. Splits on the FIRST dot; ops may contain further dots
 * (`sales.deleteSaleReturn` has none, but `settings.update.loyalty`-shaped labels do) and they
 * stay on the op, matching `splitLabel` in dualWrite.ts.
 */
function splitLabelTags(label: string): { dual_write_module: string; dual_write_op: string } {
  const i = label.indexOf('.')
  if (i === -1) return { dual_write_module: label, dual_write_op: '' }
  return { dual_write_module: label.slice(0, i), dual_write_op: label.slice(i + 1) }
}

function reportDrop(entry: OutboxEntry, reason: OutboxDropReason, error?: unknown): void {
  // ATL-1487: set only for a 409/422 — the backend's code for which kind of refusal it was.
  const refusedCode =
    error !== undefined && isRefusedWriteFailure(error) ? refusalCode(error) : undefined
  const detail = {
    label: entry.label,
    reason,
    firestoreId: entry.firestoreId,
    orgFirestoreId: entry.orgFirestoreId,
    attempts: entry.attempts,
    // ATL-908: an entry held on 401/403 never consumes an attempt, so it leaves the queue as
    // `expired` with `attempts: 0` — the SAME shape a device that was offline all week leaves.
    // Without this counter the change would move the fleet's largest drop class out of a
    // queryable reason (`attempts-exhausted`) and into a fused one, making its own before/after
    // ("did the fix work?") unmeasurable. Recorded only when non-zero so existing drops are
    // unchanged.
    ...(entry.authHolds ? { authHolds: entry.authHolds } : {}),
    // ATL-1137: WHICH refusal held it. `authHolds` alone fuses "this user was never migrated"
    // (401) with "this role may not do this" (403) — different owners, different fixes.
    ...(entry.forbidden ? { forbidden: true } : {}),
    // ATL-1207: an entry held on an unanswered recreate probe also leaves the queue as `expired`
    // with `attempts: 0` — the same shape as a device that was offline all week. Without this the
    // two are one number again, which is the fusion ATL-908's `authHolds` exists to prevent.
    ...(entry.probeHolds ? { probeHolds: entry.probeHolds } : {}),
    // ATL-1445 P8: an entry whose conversion into outbox v2 kept failing also leaves as `expired` with
    // `attempts: 0`; this keeps it distinguishable. Absent on every entry until backend PR B1.
    ...(entry.conversionHolds ? { conversionHolds: entry.conversionHolds } : {}),
    // ATL-1805: an entry held through a re-migration pause, for the same reason as the three above.
    ...(entry.pauseHolds ? { pauseHolds: entry.pauseHolds } : {}),
    // ATL-1204: an entry held on server faults, for the same reason as the four above.
    ...(entry.serverHolds ? { serverHolds: entry.serverHolds } : {}),
    ageMs: Date.now() - entry.enqueuedAt,
    ...(error !== undefined ? { error: errorMessage(error) } : {}),
    // The server's own words. Without this a `permanent-refusal` drop reports only
    // "Request failed with status code 400" — loud, and impossible to act on. See ATL-908.
    ...(() => {
      const refusal = error !== undefined ? serverRefusalDetail(error) : ''
      return refusal ? { serverRefusal: refusal } : {}
    })(),
    ...(entry.lastError ? { lastError: entry.lastError } : {}),
    // ATL-1487: WHICH kind of refusal, from the backend's machine-readable code.
    ...(refusedCode ? { refusalCode: refusedCode } : {}),
  }
  // eslint-disable-next-line no-console
  console.error(`[dualWriteOutbox] DROPPED ${entry.label} (${reason}) — Supabase write is lost`, JSON.stringify(detail))
  Sentry.captureMessage(`dual-write outbox dropped ${entry.label} (${reason})`, {
    level: 'error',
    tags: {
      dual_write_outbox_dropped: true,
      dual_write_outbox_drop_reason: reason,
      // ATL-908: `expired` alone cannot say WHY the week ran out. This tag is the filterable
      // half of `authHolds` above — Sentry cannot query a context, only a tag.
      dual_write_outbox_auth_held: Boolean(entry.authHolds),
      // ATL-1137: the filterable half of `forbidden` above — Sentry cannot query a context. An
      // expired write with this True was refused on authorization, not lost to a flat battery.
      dual_write_outbox_forbidden: Boolean(entry.forbidden),
      // ATL-1204: the filterable half of `serverHolds` above. An expired write with this True was
      // held at least once because the BACKEND failed, not only because the till was offline.
      dual_write_outbox_server_held: Boolean(entry.serverHolds),
      // ATL-1487: the refusal's backend code (`foreign_unique_conflict`, `money_leg_buried`, …),
      // filterable, on a permanent-refusal drop that was a 409/422. Absent otherwise.
      ...(refusedCode ? { dual_write_refusal_code: refusedCode } : {}),
      dual_write: entry.label,
      // ATL-983: without these, `dual_write_outbox_dropped:True dual_write_module:sales` returned
      // a FALSE ZERO — the tag simply did not exist on these events, and a Sentry query for an
      // absent tag matches nothing rather than erroring. Sales drops were there the whole time
      // (4 issues, 116 events) and the obvious query said none.
      ...splitLabelTags(entry.label),
      dual_write_org: entry.orgFirestoreId,
    },
    fingerprint: ['dual-write-outbox-dropped', entry.label, reason],
    contexts: { dual_write_outbox: detail },
  })
  // ATL-1760 — the same loss, where it can be COUNTED (see `flushDropTally`). The error that ended
  // it when there is one; otherwise (`expired`, `queue-overflow`) the last one it met, which is what
  // says why the week ran out.
  tallyDrop(entry, reason, error !== undefined ? replayFailureClass(error) : entry.lastErrorClass, refusedCode, detail.ageMs)
}

/**
 * ATL-1760 — the drops of one pass (a drain, or an enqueue that evicted), as ONE PostHog event.
 *
 * The Sentry event in `reportDrop` was the only report a drop ever had, and Sentry has refused every
 * event since its allowance ran out (ATL-1354), so a week of held writes expiring on one till was
 * invisible: 77 VSH documents were found by a set-diff, not by a reporter. A per-drop PostHog event
 * would not have fixed that. The drain starts at module load (`App.js`), before the PostHog sink is
 * registered, and the expiry check runs first in the pass, so a till offline for a week expires its
 * whole queue into the pre-registration buffer, which keeps 20 events and evicts the oldest. A
 * summary per pass is one event however many entries expired, which is what lets the case this
 * exists for survive app start.
 *
 * Counts, durations and enums only: no firestore id, no organization id (PostHog groups by
 * organization itself), no server text. `labels` are `module.op` pairs like `sales.create`.
 *
 * A pass's class is the last error an entry MET, not necessarily why it was never retried: a probe
 * or conversion hold does not update it, and an entry of another organization is never replayed by
 * this session at all (the expiry check runs before the organization check), so an `expired`
 * `transport` entry can also mean "no session of its organization drained it".
 */
interface DropTally {
  dropped: number
  // Plain null-prototype records, not Map/Set: a `.set(…)`/`.add(…)` on a member expression has a
  // Firestore write shape, and the W-writer census refuses to guess which it is (scripts/wWriterCensus.ts).
  byReason: Record<string, number>
  byClass: Record<string, number>
  labels: Record<string, true>
  refusalCodes: Record<string, true>
  maxAgeMs: number
  maxAttempts: number
  authHolds: number
  probeHolds: number
  serverHolds: number
  forbidden: number
}

type DropSource = 'drain' | 'enqueue'

/**
 * One tally per source, because an enqueue can run between two awaits of a drain: flushing a single
 * shared tally there would report half the drain's drops as the enqueue's. A reason belongs to
 * exactly one source.
 */
const DROP_SOURCE: Record<OutboxDropReason, DropSource> = {
  'permanent-refusal': 'drain',
  'attempts-exhausted': 'drain',
  expired: 'drain',
  'queue-overflow': 'enqueue',
  'entry-too-large': 'enqueue',
}

const dropTallies: Record<DropSource, DropTally | null> = { drain: null, enqueue: null }

function tallyDrop(
  entry: OutboxEntry,
  reason: OutboxDropReason,
  errorClass: ReplayFailureClass | undefined,
  refusedCode: string | undefined,
  ageMs: number,
): void {
  const source = DROP_SOURCE[reason]
  let tally = dropTallies[source]
  if (!tally) {
    tally = {
      dropped: 0,
      byReason: Object.create(null) as Record<string, number>,
      byClass: Object.create(null) as Record<string, number>,
      labels: Object.create(null) as Record<string, true>,
      refusalCodes: Object.create(null) as Record<string, true>,
      maxAgeMs: 0,
      maxAttempts: 0,
      authHolds: 0,
      probeHolds: 0,
      serverHolds: 0,
      forbidden: 0,
    }
    dropTallies[source] = tally
  }
  // A persisted class is read back from device storage; only a known one becomes a property name.
  const cls = errorClass && (REPLAY_FAILURE_CLASSES as readonly string[]).includes(errorClass) ? errorClass : 'unknown'
  tally.dropped += 1
  tally.byReason[reason] = (tally.byReason[reason] ?? 0) + 1
  tally.byClass[cls] = (tally.byClass[cls] ?? 0) + 1
  tally.labels[entry.label] = true
  if (refusedCode) tally.refusalCodes[refusedCode] = true
  tally.maxAgeMs = Math.max(tally.maxAgeMs, ageMs)
  tally.maxAttempts = Math.max(tally.maxAttempts, entry.attempts)
  tally.authHolds += entry.authHolds ?? 0
  tally.probeHolds += entry.probeHolds ?? 0
  // ATL-1204: an entry that spent its week in server-fault holds and last met a transport error is
  // tallied as class_transport; this is what says the backend, not the till, kept it from landing.
  tally.serverHolds += entry.serverHolds ?? 0
  if (entry.forbidden) tally.forbidden += 1
}

/** `queue-overflow` → `queue_overflow`: a property-name-safe key. */
const tallyKey = (prefix: string, value: string): string => `${prefix}_${value.replace(/-/g, '_')}`

function flushDropTally(source: DropSource): void {
  const tally = dropTallies[source]
  dropTallies[source] = null
  if (!tally) return
  const counts: Record<string, number> = {}
  for (const [reason, n] of Object.entries(tally.byReason)) counts[tallyKey('reason', reason)] = n
  for (const [cls, n] of Object.entries(tally.byClass)) counts[tallyKey('class', cls)] = n
  captureOperationalEvent('dual_write_outbox_dropped', {
    dual_write_outbox_dropped: tally.dropped,
    source,
    reasons: Object.keys(tally.byReason).sort(),
    lastErrorClasses: Object.keys(tally.byClass).sort(),
    labels: Object.keys(tally.labels).sort(),
    refusalCodes: Object.keys(tally.refusalCodes).sort(),
    ...counts,
    maxAgeMs: tally.maxAgeMs,
    maxAttempts: tally.maxAttempts,
    authHolds: tally.authHolds,
    probeHolds: tally.probeHolds,
    serverHolds: tally.serverHolds,
    forbidden: tally.forbidden,
  })
}

// ============================================================================
// Introspection (debug/support tooling)
// ============================================================================

export async function getOutboxSnapshot(): Promise<OutboxEntry[]> {
  return withQueue(async (queue) => ({
    queue,
    result: queue.map((e) => ({ ...e, request: { ...e.request } })),
  }))
}
